// Website-specific translations that sit alongside the existing app i18n.
// Language state is shared via the same localStorage key used by the app.

import {
  getSavedLanguage,
  setSavedLanguage,
  LANGUAGE_OPTIONS,
  type SupportedLanguage,
} from '../src/functions/languageSettings';

export { getSavedLanguage, setSavedLanguage, LANGUAGE_OPTIONS };
export type { SupportedLanguage };

// ── Translation keys ─────────────────────────────────────────────────────────
export interface WebsiteTranslations {
  // Site metadata
  'site.titleSuffix': string;
  'site.description': string;
  // Nav
  'nav.features': string;
  'nav.pricing': string;
  'nav.stories': string;
  'nav.login': string;
  'nav.signup': string;
  'nav.blog': string;
  // Hero
  'hero.eyebrow': string;
  'hero.headline0': string;
  'hero.headline1': string;
  'hero.headline2': string;
  'hero.headline3': string;
  'hero.headline4': string;
  'hero.body': string;
  'hero.cta': string;
  'hero.cta.openApp': string;
  'hero.cta.installOn': string;
  'hero.cta.installApp': string;
  'hero.installTip.ios': string;
  'hero.installTip.android': string;
  'hero.installTip.default': string;
  // Install modal
  'install.modal.title': string;
  'install.modal.subtitle': string;
  'install.modal.android.cta': string;
  'install.modal.android.hint': string;
  'install.modal.android.step1': string;
  'install.modal.android.step2': string;
  'install.modal.ios.step1': string;
  'install.modal.ios.step2': string;
  'install.modal.ios.step3': string;
  'install.modal.ios.hint': string;
  'install.modal.desktop.tip': string;
  'install.modal.feature1': string;
  'install.modal.feature2': string;
  'install.modal.feature3': string;
  'install.modal.dismiss': string;
  // Features
  'features.eyebrow': string;
  'features.heading': string;
  'features.subheading': string;
  'features.vitals.title': string;
  'features.vitals.desc': string;
  'features.meds.title': string;
  'features.meds.desc': string;
  'features.guide.title': string;
  'features.guide.desc': string;
  'features.notifications.title': string;
  'features.notifications.desc': string;
  'features.trends.title': string;
  'features.trends.desc': string;
  'features.rewards.title': string;
  'features.rewards.desc': string;
  // Pricing
  'pricing.heading': string;
  'pricing.subheading': string;
  'pricing.monthly': string;
  'pricing.yearly': string;
  'pricing.mostPopular': string;
  'pricing.perMonth': string;
  'pricing.choosePlan': string;
  'pricing.startFree': string;
  'pricing.currentPlan': string;
  'pricing.free.name': string;
  'pricing.free.price': string;
  'pricing.free.subtitle': string;
  'pricing.vital.name': string;
  'pricing.vital.subtitle': string;
  'pricing.lifetime.name': string;
  'pricing.lifetime.subtitle': string;
  'pricing.feat.vitals': string;
  'pricing.feat.meds': string;
  'pricing.feat.notifications': string;
  'pricing.feat.guide': string;
  'pricing.feat.rewards': string;
  'pricing.feat.endlessRecords': string;
  'pricing.feat.vault5': string;
  'pricing.feat.vault50': string;
  'pricing.feat.vault150': string;
  'pricing.feat.trends': string;
  'pricing.feat.customTrends': string;
  'pricing.feat.digest': string;
  'pricing.feat.onePet': string;
  'pricing.feat.vitalPets': string;
  'pricing.feat.coCarers': string;
  'pricing.feat.fellowship': string;
  'pricing.feat.unlimitedPets': string;
  'pricing.feat.vetExport': string;
  // Blog
  'blog.heading': string;
  'blog.subheading': string;
  'blog.readMore': string;
  'blog.backToBlog': string;
  'blog.minRead': string;
  'blog.moreFromTeam': string;
  'blog.readAllArticles': string;
  'blog.tag.newNorms': string;
  'blog.tag.roomies': string;
  'blog.tag.labs': string;
  'blog.tag.routines': string;
  'blog.authorTeam': string;
  // Legal pages
  'legal.eyebrow': string;
  'legal.lastUpdated': string;
  'legal.privacyTitle': string;
  'legal.termsTitle': string;
  // FAQ
  'faq.heading': string;
  'faq.subheading': string;
  'faq.stillHaveQuestions': string;
  'faq.contactUs': string;
  'faq.emailSupport': string;
  'faq.category.gettingStarted': string;
  'faq.category.vitals': string;
  'faq.category.meds': string;
  'faq.category.guide': string;
  'faq.category.notifications': string;
  'faq.category.roomies': string;
  'faq.category.trends': string;
  'faq.category.security': string;
  'faq.category.pricing': string;
  'faq.category.technical': string;
  'faq.q.whatIsKodaCare': string;
  'faq.q.whoCanUse': string;
  'faq.q.howToSignUp': string;
  'faq.q.techSkills': string;
  'faq.q.whatAreVitals': string;
  'faq.q.howOftenLog': string;
  'faq.q.normalRanges': string;
  'faq.q.fluidIntake': string;
  'faq.q.editDeleteLogs': string;
  'faq.q.addMedication': string;
  'faq.q.immunosuppressants': string;
  'faq.q.reminders': string;
  'faq.q.missedDose': string;
  'faq.q.multipleUsers': string;
  'faq.q.whatInGuide': string;
  'faq.q.languages': string;
  'faq.q.guideAccuracy': string;
  'faq.q.updateGuide': string;
  'faq.q.notificationTypes': string;
  'faq.q.customizeNotifications': string;
  'faq.q.twoNotifications': string;
  'faq.q.whatIsKoda': string;
  'faq.q.earnXP': string;
  'faq.q.streaks': string;
  'faq.q.whatAreRoomies': string;
  'faq.q.joinRoomies': string;
  'faq.q.privacy': string;
  'faq.q.leaveRoomies': string;
  'faq.q.whatAreTrends': string;
  'faq.q.shareData': string;
  'faq.q.dataDownload': string;
  'faq.q.dataEncrypted': string;
  'faq.q.whoAccess': string;
  'faq.q.compliance': string;
  'faq.q.deleteAccount': string;
  'faq.q.dataRetention': string;
  'faq.q.freePlan': string;
  'faq.q.planDifference': string;
  'faq.q.upgrade': string;
  'faq.q.refund': string;
  'faq.q.devices': string;
  'faq.q.offline': string;
  'faq.q.syncData': string;
  'faq.q.support': string;
  'faq.a.whatIsKodaCare': string;
  'faq.a.whoCanUse': string;
  'faq.a.howToSignUp': string;
  'faq.a.techSkills': string;
  'faq.a.whatAreVitals': string;
  'faq.a.howOftenLog': string;
  'faq.a.normalRanges': string;
  'faq.a.fluidIntake': string;
  'faq.a.editDeleteLogs': string;
  'faq.a.addMedication': string;
  'faq.a.immunosuppressants': string;
  'faq.a.reminders': string;
  'faq.a.missedDose': string;
  'faq.a.multipleUsers': string;
  'faq.a.whatInGuide': string;
  'faq.a.languages': string;
  'faq.a.guideAccuracy': string;
  'faq.a.updateGuide': string;
  'faq.a.notificationTypes': string;
  'faq.a.customizeNotifications': string;
  'faq.a.twoNotifications': string;
  'faq.a.whatIsKoda': string;
  'faq.a.earnXP': string;
  'faq.a.streaks': string;
  'faq.a.whatAreRoomies': string;
  'faq.a.joinRoomies': string;
  'faq.a.privacy': string;
  'faq.a.leaveRoomies': string;
  'faq.a.whatAreTrends': string;
  'faq.a.shareData': string;
  'faq.a.dataDownload': string;
  'faq.a.dataEncrypted': string;
  'faq.a.whoAccess': string;
  'faq.a.compliance': string;
  'faq.a.deleteAccount': string;
  'faq.a.dataRetention': string;
  'faq.a.freePlan': string;
  'faq.a.planDifference': string;
  'faq.a.upgrade': string;
  'faq.a.refund': string;
  'faq.a.devices': string;
  'faq.a.offline': string;
  'faq.a.syncData': string;
  'faq.a.support': string;
  // Personas
  'personas.eyebrow': string;
  'personas.heading': string;
  'personas.subheading': string;
  'personas.modern.title': string;
  'personas.modern.tag': string;
  'personas.modern.desc': string;
  'personas.elderly.title': string;
  'personas.elderly.tag': string;
  'personas.elderly.desc': string;
  'personas.farm.title': string;
  'personas.farm.tag': string;
  'personas.farm.desc': string;
  // Footer
  'footer.tagline': string;
  'footer.disclaimer': string;
  'footer.product': string;
  'footer.account': string;
  'footer.legal': string;
  'footer.terms': string;
  'footer.privacy': string;
  'footer.faq': string;
  'footer.rightsReserved': string;
  // Cookie / GDPR consent bar
  'gdpr.title': string;
  'gdpr.body': string;
  'gdpr.learnMore': string;
  'gdpr.accept': string;
  'gdpr.decline': string;
}

const en: WebsiteTranslations = {
  'site.titleSuffix': 'Pet Health Companion',
  'site.description':
    'KodaCare helps pet parents track medications, water intake, temperature, routines, and wellbeing — with practical guidance in one place.',
  'nav.features': 'FEATURES',
  'nav.pricing': 'PRICING',
  'nav.stories': 'STORIES',
  'nav.login': 'Login',
  'nav.signup': 'Sign up',
  'nav.blog': 'Blog',
  'hero.eyebrow': 'TAKE THE LEAD WITH KODA.',
  'hero.headline0': 'Take',
  'hero.headline1': 'the',
  'hero.headline2': 'Lead with',
  'hero.headline3': 'Koda',
  'hero.headline4': '',
  'hero.body':
    'Track vitals, learn expert pet care, and spot health shifts. KodaCare empowers hoomans—because more awareness means a healthier pet.',
  'hero.cta': 'Try for Free',
  'hero.cta.openApp': 'Open the App',
  'hero.cta.installOn': 'Install on',
  'hero.cta.installApp': 'Install the App',
  'hero.installTip.ios': "Tap the Share button in Safari, then choose 'Add to Home Screen'.",
  'hero.installTip.android': "Tap the browser menu and choose 'Add to Home Screen' or 'Install App'.",
  'hero.installTip.default': "Look for the install icon (⊕) in your browser's address bar and click it to install.",
  'install.modal.title': 'Add KodaCare to your device',
  'install.modal.subtitle': 'Get the full app experience:',
  'install.modal.feature1': 'Offline Access',
  'install.modal.feature2': 'Push Notifications',
  'install.modal.feature3': 'Instant Launch',
  'install.modal.android.cta': 'Install',
  'install.modal.android.hint': "Tap the button above and confirm in the prompt that appears.",
  'install.modal.android.step1': "Tap the browser menu (⋮)",
  'install.modal.android.step2': "Tap 'Add to Home Screen'",
  'install.modal.ios.step1': 'Tap the Share button at the bottom of your screen',
  'install.modal.ios.step2': "Scroll down and tap 'Add to Home Screen'",
  'install.modal.ios.step3': "Tap 'Add' in the top-right corner to confirm",
  'install.modal.ios.hint': 'Use Safari for the best install experience on iOS.',
  'install.modal.desktop.tip': "Look for the install icon in your browser's address bar (it looks like a screen with a download arrow) and click it to add KodaCare to your desktop.",
  'install.modal.dismiss': 'Maybe later',
  'features.eyebrow': 'THE KODA TOOLKIT',
  'features.heading': 'Designed for Every Companion',
  'features.subheading':
    'Professional tools, gamified for your everyday routine.',
  'features.vitals.title': 'Vet-Level Vital Tracking',
  'features.vitals.desc':
    'Log vitals with medical precision. View high-resolution Trend Graphs to spot patterns before they become problems.',
  'features.meds.title': 'The "No-Confusion" Med Kit',
  'features.meds.desc':
    'Track every medication with visual pill shapes, dosage details, and daily time slots. Smart reminders make sure nothing gets missed — no guesswork, no double-dosing.',
  'features.guide.title': 'Co-Carers & Notifications',
  'features.guide.desc':
    'Invite family members to help manage your pet\'s health. With reminders know who did what and when, and never miss another med.',
  'features.notifications.title': 'The Koda Fellowship',
  'features.notifications.desc':
    'Level up your pet knowledge with guides and pro tips. Every article read earns you XP for the Fellowship.',
  'features.trends.title': 'Emergency Vet Finder',
  'features.trends.desc':
    'Instant GPS-linked access to the nearest 24/7 clinics, complete with contact info and turn-by-turn directions.',
  'features.rewards.title': 'The Vet Dashboard',
  'features.rewards.desc':
    'Coming soon — live-sync your data directly with your veterinarian. No more manual exports; just real-time professional oversight.',
  'pricing.heading': 'Choose Your Fellowship',
  'pricing.subheading':
    'Transparent pricing for elite care. No hidden fees, just happy tails.',
  'pricing.monthly': 'MONTHLY',
  'pricing.yearly': 'YEARLY',
  'pricing.mostPopular': 'MOST POPULAR',
  'pricing.perMonth': '/mo',
  'pricing.choosePlan': 'Choose plan',
  'pricing.startFree': 'Get started free',
  'pricing.currentPlan': 'Your current plan',
  'pricing.free.name': 'Starter',
  'pricing.free.price': 'FREE',
  'pricing.free.subtitle': 'Perfect for the new pet owner finding their paws.',
  'pricing.vital.name': 'Vital Companion',
  'pricing.vital.subtitle': 'The gold standard for families and multi-pet homes.',
  'pricing.lifetime.name': 'Expert Companion',
  'pricing.lifetime.subtitle': 'The ultimate pack for Pet Sitters, Foster Carers and Homesteaders.',
  'pricing.feat.vault5': 'Vet Records Vault (5 files)',
  'pricing.feat.customTrends': 'Customisable Extended Trends',
  'pricing.feat.vitals': 'Pet Vitals Logbook',
  'pricing.feat.meds': 'Medication & Care Schedule',
  'pricing.feat.notifications': 'Notifications',
  'pricing.feat.guide': 'Pet Care Library',
  'pricing.feat.rewards': 'Streaks & Pet Parents',
  'pricing.feat.endlessRecords': 'Endless Records',
  'pricing.feat.vault50': 'Vet Records Vault (up to 50 files)',
  'pricing.feat.vault150': 'Vet Records Vault (150 files)',
  'pricing.feat.trends': 'Trends & Charts',
  'pricing.feat.digest': 'Monthly Pet Health Digest',
  'pricing.feat.onePet': '1 Pet Profile',
  'pricing.feat.vitalPets': 'Up to 4 Pet Profiles',
  'pricing.feat.coCarers': '2 co-carers per pet',
  'pricing.feat.fellowship': 'The Koda Fellowship',
  'pricing.feat.unlimitedPets': 'Up to 30 Pet Profiles',
  'pricing.feat.vetExport': 'Vet-Ready Data Exports',
  'blog.heading': 'From the KodaCare team',
  'blog.subheading': 'Insights, updates and stories from our community.',
  'blog.readMore': 'Read more →',
  'blog.backToBlog': 'Back to Blog',
  'blog.minRead': 'min read',
  'blog.moreFromTeam': 'More from the KodaCare team',
  'blog.readAllArticles': 'Read all articles',
  'blog.tag.newNorms': 'New Norms',
  'blog.tag.roomies': 'Roomies',
  'blog.tag.labs': 'Labs',
  'blog.tag.routines': 'Routines',
  'blog.authorTeam': 'The KodaCare Team',
  'legal.eyebrow': 'Legal',
  'legal.lastUpdated': 'Last updated',
  'legal.privacyTitle': 'Privacy Policy',
  'legal.termsTitle': 'Terms of Service',
  // FAQ
  'faq.heading': 'Frequently Asked Questions',
  'faq.subheading': 'Everything you need to know about KodaCare. If you don\'t find your answer here, please reach out to our support team.',
  'faq.stillHaveQuestions': 'Still have questions?',
  'faq.contactUs': 'Our support team is here to help you get the most out of KodaCare.',
  'faq.emailSupport': 'Contact Support',
  'faq.category.gettingStarted': 'Getting Started',
  'faq.category.vitals': 'Vitals Logbook',
  'faq.category.meds': 'Medication Centre',
  'faq.category.guide': 'Digital Guide',
  'faq.category.notifications': 'Notifications & Rewards',
  'faq.category.roomies': 'Roomies & Community',
  'faq.category.trends': 'Trends & Data',
  'faq.category.security': 'Privacy & Security',
  'faq.category.pricing': 'Pricing & Features',
  'faq.category.technical': 'Technical Support',
  // Getting Started
  'faq.q.whatIsKodaCare': 'What is KodaCare?',
  'faq.a.whatIsKodaCare': 'KodaCare is a comprehensive pet health companion for modern pet parents. It helps you track pet vitals, manage medications and care schedules, organise vet records, and access practical guidance in one secure, easy-to-use app.',
  'faq.q.whoCanUse': 'Who can use KodaCare?',
  'faq.a.whoCanUse': 'KodaCare is designed for pet parents, carers, and households managing a pet\'s ongoing health routine. Anyone aged 18+ can create an account and track one or more companions.',
  'faq.q.howToSignUp': 'How do I sign up?',
  'faq.a.howToSignUp': 'Visit the KodaCare app or website and tap "Sign Up." Enter your email address and create a secure password. You\'ll receive a verification email—click the link to confirm your account. Then complete your pet profile and start logging.',
  'faq.q.techSkills': 'Do I need to be tech-savvy to use KodaCare?',
  'faq.a.techSkills': 'Not at all! KodaCare is designed to be intuitive and user-friendly for everyone, regardless of technical experience. If you ever get stuck, our support team is always available to help.',
  // Vitals Logbook
  'faq.q.whatAreVitals': 'What are vital signs and why do I log them?',
  'faq.a.whatAreVitals': 'Pet vitals are day-to-day health indicators such as weight, temperature, water intake, and food portions. Tracking these consistently helps you notice meaningful changes early and share clearer context with your vet during visits.',
  'faq.q.howOftenLog': 'How often should I log my vitals?',
  'faq.a.howOftenLog': 'Your vet can guide what matters most for your companion, but many pet parents log once or twice daily depending on the care plan. Consistency creates a clearer picture over time, and KodaCare reminders help you stay on track.',
  'faq.q.normalRanges': 'What are the normal ranges for vital signs?',
  'faq.a.normalRanges': 'Normal ranges vary by species, breed, age, and individual health history. Your vet is the best source for what\'s normal for your companion. KodaCare is a tracking tool and does not replace professional veterinary advice.',
  'faq.q.fluidIntake': 'Why does the app ask about fluid intake?',
  'faq.a.fluidIntake': 'Water intake is a useful day-to-day signal for pet wellbeing. Changes in thirst patterns can be important context for your vet, especially when viewed alongside weight, appetite, and temperature trends.',
  'faq.q.editDeleteLogs': 'Can I edit or delete a vital log entry?',
  'faq.a.editDeleteLogs': 'Yes. You can edit or delete entries if you make a mistake. Open the Records section, select the date, and choose the entry you want to update.',
  // Medication Centre
  'faq.q.addMedication': 'How do I add my medications to the app?',
  'faq.a.addMedication': 'Go to the Meds section and tap "Add Medication." Enter the name, dosage, frequency, and any care notes. You can also schedule recurring routines like flea, tick, or worming treatments.',
  'faq.q.immunosuppressants': 'What are immunosuppressants and why are they so important?',
  'faq.a.immunosuppressants': 'For pets on immunosuppressants, these medications help manage immune responses for specific conditions. Missing doses can reduce treatment effectiveness, so use reminders and follow your vet\'s instructions closely.',
  'faq.q.reminders': 'How do I set up medication reminders?',
  'faq.a.reminders': 'When you add a medication or routine, you\'ll see options to set reminder times. KodaCare sends gentle push notifications so your pet-care routine stays consistent.',
  'faq.q.missedDose': 'What should I do if I miss a dose?',
  'faq.a.missedDose': 'Log it in the app and contact your vet for guidance if needed. Don\'t double-dose to make up for it unless your vet advises it. KodaCare lets you mark doses as "missed" for accurate records.',
  'faq.q.multipleUsers': 'Can one device be used by multiple patients?',
  'faq.a.multipleUsers': 'Each account is tied to one household profile for privacy and accuracy. If multiple family members use KodaCare, each person can create their own account and collaborate on the same pet routines.',
  // Digital Guide
  'faq.q.whatInGuide': 'What information is in the Digital Guide?',
  'faq.a.whatInGuide': 'The Digital Guide is a comprehensive post-transplant handbook covering five essential topics: Food (dietary guidelines and restrictions), Hygiene (infection prevention and personal care), Exercise (safe activity levels during recovery), Medication (how to take your meds correctly), and Obstacles (common challenges and how to handle them). Each section is evidence-based and reviewed for accuracy.',
  'faq.q.languages': 'What languages is the app available in?',
  'faq.a.languages': 'KodaCare is available in 5 languages: English, French, Spanish, German, and Portuguese. You can change your language in Settings and the app will update instantly.',
  'faq.q.guideAccuracy': 'How current is the information in the guide?',
  'faq.a.guideAccuracy': 'The guide is created with input from transplant clinicians and updated regularly to reflect current post-transplant best practices. However, always follow your transplant team\'s specific advice for you—individual protocols vary. The guide is educational, not a substitute for medical advice.',
  'faq.q.updateGuide': 'Does the guide get updated?',
  'faq.a.updateGuide': 'Yes, we update the Digital Guide periodically to include new research and feedback from our community. Updates are delivered automatically through the app—you don\'t need to do anything.',
  // Notifications & Rewards
  'faq.q.notificationTypes': 'What kind of notifications will I receive?',
  'faq.a.notificationTypes': 'KodaCare sends two types of notifications: medication reminders (at your set times each day) and vital log reminders (suggesting when to record daily measurements). These are intentionally subtle—no loud alarms. You can customize or disable notifications in Settings.',
  'faq.q.customizeNotifications': 'Can I turn off notifications?',
  'faq.a.customizeNotifications': 'Absolutely. You can disable all notifications, or customize individual ones. Go to Settings → Notifications and toggle each type on or off. We recommend keeping medication reminders on, but the choice is yours.',
  'faq.q.twoNotifications': 'Why am I getting two notifications at the same time?',
  'faq.a.twoNotifications': 'This is completely normal when you first set up KodaCare. Your device sends two separate permission prompts — one from the browser and one from the app itself. If you approved both, you may briefly receive duplicate notifications. They will settle into one after a short time. If duplicates persist, go to Settings → Notifications, turn them off and back on again to reset.',
  'faq.q.whatIsKoda': 'What is Koda and how does it work?',
  'faq.a.whatIsKoda': 'Koda is KodaCare\'s rewards system designed to keep you motivated and accountable. You earn XP (experience points) for healthy habits like logging vitals, recording medications, reading guide articles, and maintaining daily streaks.',
  'faq.q.earnXP': 'How do I earn XP?',
  'faq.a.earnXP': 'You earn XP for: logging your vitals (+15 XP), recording medications (+10 XP), reading guide articles (+10 XP), and hitting streak milestones at 7, 14, and 30 days (+50 XP each). Each action only awards XP once per calendar day, so you can\'t "game" the system.',
  'faq.q.streaks': 'What\'s a streak and why does it matter?',
  'faq.a.streaks': 'A streak is a continuous count of days you\'ve logged your vitals. Building streaks reinforces consistency. KodaCare celebrates your streaks with bonuses at 7, 14, and 30 days. Your streak calendar gives you a clear visual history.',
  // Roomies & Community
  'faq.q.whatAreRoomies': 'What are Roomies?',
  'faq.a.whatAreRoomies': 'Roomies are other pet parents you\'re connected with. In KodaCare, you can form a Roomies group to see shared progress (with consent), celebrate wins, and support each other.',
  'faq.q.joinRoomies': 'How do I join or create a Roomies group?',
  'faq.a.joinRoomies': 'Go to Settings → Roomies. Create or enter a group code (you\'ll share this code with people you want to connect with). Choose a nickname and Koda color that represents you. Toggle "Enable Roomies" to opt into the group feed.',
  'faq.q.privacy': 'Is my data private if I join a Roomies group?',
  'faq.a.privacy': 'Yes. You must explicitly toggle "Enable Roomies" to share data, and you choose exactly what group code you join. Only people in your group with Roomies enabled will see basic activity (like "logged vitals today" or "hit 7-day streak"). Your actual vital numbers, medication details, and personal health data are never shared. Roomies is about connection, not data exposure.',
  'faq.q.leaveRoomies': 'Can I leave a Roomies group or turn it off?',
  'faq.a.leaveRoomies': 'Yes. Go to Settings → Roomies and toggle "Enable Roomies" off at any time. You\'ll immediately stop sharing and seeing the group feed. You can also change your group code to join a different group.',
  // Trends & Data
  'faq.q.whatAreTrends': 'What are Trends and how do I view them?',
  'faq.a.whatAreTrends': 'Trends turn your logged data into clear, easy-to-read charts. Go to the Trends section to see graphs of weight, temperature, intake, and other vitals over time.',
  'faq.q.shareData': 'Can I share my data with my vet?',
  'faq.a.shareData': 'Yes. You can export or share your Trends report directly from the app to send to your doctor. This is especially helpful before appointments so your team can review your complete health picture.',
  'faq.q.dataDownload': 'Can I download all my health data?',
  'faq.a.dataDownload': 'Yes, you can download a copy of all your logged data as a file. Go to Settings → Data and select "Download My Data." This gives you ownership and portability of your health information.',
  // Privacy & Security
  'faq.q.dataEncrypted': 'Is my health data encrypted?',
  'faq.a.dataEncrypted': 'Yes. KodaCare uses industry-standard encryption (TLS/SSL) to protect your data in transit and at rest.',
  'faq.q.whoAccess': 'Who can access my health information?',
  'faq.a.whoAccess': 'Only you can access your full health data. If you enable Roomies, your groupmates see limited activity (like "logged vitals today") but not your actual measurements. You control what data you share with your doctor or family through explicit export/sharing actions.',
  'faq.q.compliance': 'Is KodaCare compliant with privacy laws?',
  'faq.a.compliance': 'Yes. KodaCare complies with GDPR (EU) and UK GDPR. Your data is treated with the highest level of care.',
  'faq.q.deleteAccount': 'Can I delete my account and data?',
  'faq.a.deleteAccount': 'Yes. Go to Settings → Account and select "Delete Account." Your account and all associated data will be permanently removed within 30 days. You can also download a copy of your data before deletion.',
  'faq.q.dataRetention': 'How long is my data kept?',
  'faq.a.dataRetention': 'Active account data is retained for the life of your account. If you delete your account, your personal data is removed within 30 days. Anonymised aggregate records may be kept for up to 6 months for medical device compliance obligations. Authentication logs are kept for 90 days, analytics data for up to 26 months, and cookie consent records for 12 months. For full details see our Privacy Policy.',
  // Pricing & Features
  'faq.q.freePlan': 'How long can I use KodaCare for free?',
  'faq.a.freePlan': 'The free plan includes full access to all core features for your first 15 days. After 15 days, you\'ll need to choose a paid plan to continue logging. The free trial lets you get comfortable with the app before committing.',
  'faq.q.planDifference': 'What\'s the difference between the Vital Companion and Expert Companion plans?',
  'faq.a.planDifference': 'Vital Companion gives you everything — endless records, full app access, and a Document Vault for up to 20 files. Expert Companion adds customisable trend charts (build and edit your own graphs), a larger Document Vault for up to 150 files, and a monthly curated digest of the latest nephrology research and news so you\'re always informed heading into your clinic appointments.',
  'faq.q.upgrade': 'How do I upgrade my plan?',
  'faq.a.upgrade': 'After your 15-day free trial, the app will prompt you to choose a plan. Tap the plan you want and complete the payment. You can also upgrade anytime in Settings → Subscription.',
  'faq.q.refund': 'What\'s your refund policy?',
  'faq.a.refund': 'If you\'re not satisfied within 30 days of purchase, we offer a full refund—no questions asked. Contact our support team to request a refund.',
  // Technical
  'faq.q.devices': 'What devices can I use KodaCare on?',
  'faq.a.devices': 'KodaCare works on iPhone, Android phones, iPad, and web browsers. You can use the app on multiple devices—just log in with the same account and your data syncs automatically.',
  'faq.q.offline': 'Can I use KodaCare offline?',
  'faq.a.offline': 'Yes! The app stores data locally on your device, so you can log your vitals and medications even without internet. When you reconnect, everything syncs to the cloud automatically.',
  'faq.q.syncData': 'How does data sync across my devices?',
  'faq.a.syncData': 'When you log in on multiple devices, KodaCare syncs your data in real-time. If you\'re offline on one device, changes sync when you reconnect. This ensures you always have access to your most recent information.',
  'faq.q.support': 'How do I get help if I have a problem?',
  'faq.a.support': 'Use the Help section in Settings within the app. Our team typically responds within 24 hours.',
  'personas.eyebrow': 'DESIGNED FOR EVERY COMPANION',
  'personas.heading': 'Designed for Every Companion',
  'personas.subheading': 'KodaCare adapts to your life, whether you have one pup or a whole pasture.',
  'personas.modern.title': 'The Modern Pet Parent',
  'personas.modern.tag': '"Peace of Mind"',
  'personas.modern.desc': 'Gamified habits that turn daily care into a win. Perfect for tracking streaks and sharing milestones in the Fellowship.',
  'personas.elderly.title': 'The Elderly Dog Carer',
  'personas.elderly.tag': '"The Safety Net"',
  'personas.elderly.desc': 'Simple, bold visuals and multi-user alerts so the whole family can support the care of a beloved senior pet.',
  'personas.farm.title': 'The Modern Farm Keeper',
  'personas.farm.tag': '"The Herd Hub"',
  'personas.farm.desc': 'Professional-grade data management for multiple companions. Track herd health trends and export professional reports for your vet.',
  'footer.tagline': 'The digital home for everyday pet care.',
  'footer.disclaimer':
    'KodaCare is a pet health tracking tool only. It does not provide veterinary advice, diagnosis, or treatment.',
  'footer.product': 'Product',
  'footer.account': 'Account',
  'footer.legal': 'Legal',
  'footer.terms': 'Terms & Conditions',
  'footer.privacy': 'Privacy Policy',
  'footer.faq': 'FAQ',
  'footer.rightsReserved': 'All rights reserved.',
  'gdpr.title': 'We use cookies',
  'gdpr.body': 'We use essential cookies to keep you signed in and remember your preferences. We also use analytics cookies to understand how KodaCare is used so we can improve it. No personal pet health data is ever shared with third-party advertisers.',
  'gdpr.learnMore': 'Learn more in our Privacy Policy',
  'gdpr.accept': 'Accept all',
  'gdpr.decline': 'Essential only',
};

const fr: WebsiteTranslations = {
  'site.titleSuffix': 'Compagnon santé pour la greffe rénale',
  'site.description':
    'KodaCare aide les pet parents à suivre les médicaments, l’hydratation et la santé quotidienne de leur compagnon, avec des conseils pratiques et des tableaux de suivi.',
  'nav.features': 'FONCTIONS',
  'nav.pricing': 'TARIFS',
  'nav.stories': 'TÉMOIGNAGES',
  'nav.login': 'Connexion',
  'nav.signup': "S'inscrire",
  'nav.blog': 'Blog',
  'hero.eyebrow': 'CENTRE PATIENT POST-GREFFE',
  'hero.headline0': '',
  'hero.headline1': 'La récupération est',
  'hero.headline2': 'un marathon.',
  'hero.headline3': 'Ne le courez pas',
  'hero.headline4': 'sans KodaCare.',
  'hero.body':
    "Le soin complet de votre animal, simplifié. KodaCare vous aide à suivre les médicaments et les tendances de santé de chaque animal chez vous. Restez sur la bonne voie et grimpez dans les rangs de notre Fellowship passionnée d'animaux.",
  'hero.cta': 'Essayer gratuitement',
  'hero.cta.openApp': "Ouvrir l'application",
  'hero.cta.installOn': 'Installer sur',
  'hero.cta.installApp': "Installer l'application",
  'hero.installTip.ios': "Touchez Partager dans Safari, puis choisissez « Ajouter à l’écran d’accueil ».",
  'hero.installTip.android': "Ouvrez le menu du navigateur puis choisissez « Ajouter à l’écran d’accueil » ou « Installer l’application ».",
  'hero.installTip.default': "Recherchez l’icône d’installation (⊕) dans la barre d’adresse de votre navigateur et cliquez dessus.",
  'install.modal.title': 'Ajouter KodaCare à votre appareil',
  'install.modal.subtitle': "Profitez de l’expérience complète :",
  'install.modal.android.cta': 'Installer',
  'install.modal.feature1': 'Accès hors ligne',
  'install.modal.feature2': 'Notifications push',
  'install.modal.feature3': 'Lancement instantané',
  'install.modal.android.hint': "Appuyez sur le bouton ci-dessus et confirmez dans l’invite qui s’affiche.",
  'install.modal.ios.step1': "Appuyez sur le bouton Partager en bas de l’écran",
  'install.modal.ios.step2': "Faites défiler vers le bas et appuyez sur ‘Sur l’écran d’accueil’",
  'install.modal.ios.step3': "Appuyez sur ‘Ajouter’ en haut à droite pour confirmer",
  'install.modal.ios.hint': "Utilisez Safari pour une meilleure expérience d’installation sur iOS.",
  'install.modal.desktop.tip': "Recherchez l’icône d’installation dans la barre d’adresse (elle ressemble à un écran avec une flèche de téléchargement) et cliquez pour ajouter KodaCare à votre bureau.",
  'install.modal.dismiss': 'Plus tard',
  'features.eyebrow': 'TOUT CE DONT VOUS AVEZ BESOIN',
  'features.heading': 'Conçu pour la vie après la greffe',
  'features.subheading':
    'KodaCare réunit tous les outils nécessaires pour gérer la routine de soin de votre compagnon.',
  'features.vitals.title': 'Journal des constantes',
  'features.vitals.desc':
    "Enregistrez la tension artérielle, le poids, la température, les apports liquidiens et la diurèse deux fois par jour.",
  'features.meds.title': 'Centre médicaments',
  'features.meds.desc':
    'Suivez chaque médicament avec des formes visuelles, les dosages et les créneaux horaires. Des rappels intelligents pour ne rien oublier — sans confusion ni double dose.',
  'features.guide.title': 'Guide numérique',
  'features.guide.desc':
    'Un manuel post-greffe complet couvrant alimentation, hygiène, exercice, obstacles et contacts — en 5 langues.',
  'features.notifications.title': 'Notifications intelligentes',
  'features.notifications.desc':
    'Des rappels pour vos journaux de constantes et votre programme médicamenteux.',
  'features.trends.title': 'Tendances',
  'features.trends.desc':
    'De beaux graphiques transforment vos données brutes en tendances significatives.',
  'features.rewards.title': 'Récompenses & Colocataires',
  'features.rewards.desc':
    "Restez motivé avec des récompenses de régularité et invitez un membre de la famille.",
  'pricing.heading': 'Plans & Tarifs',
  'pricing.subheading': 'Commencez gratuitement pendant 15 jours. Passez à la version supérieure pour conserver vos données et accéder à des enregistrements illimités.',
  'pricing.monthly': 'MENSUEL',
  'pricing.yearly': 'ANNUEL',
  'pricing.mostPopular': 'LE PLUS POPULAIRE',
  'pricing.perMonth': '/mois',
  'pricing.choosePlan': 'Choisir',
  'pricing.startFree': 'Commencer gratuitement',
  'pricing.currentPlan': 'Votre plan actuel',
  'pricing.free.name': 'Débutant',
  'pricing.free.price': 'GRATUIT',
  'pricing.free.subtitle': 'Essai de 15 jours — toutes les fonctionnalités incluses. Après 15 jours, passez à la version supérieure pour conserver vos données.',
  'pricing.vital.name': 'Compagnon Vital',
  'pricing.vital.subtitle': 'Enregistrements illimités, stockage de documents et accès complet — tout ce dont vous avez besoin à long terme.',
  'pricing.lifetime.name': 'Expert Companion',
  'pricing.lifetime.subtitle': 'Tout ce qu\'il y a dans Vital, plus des graphiques de tendances personnalisables, un coffre-fort de documents plus grand et un digest mensuel des dernières actualités en néphrologie.',
  'pricing.feat.vitals': 'Journal des constantes',
  'pricing.feat.meds': 'Centre médicaments',
  'pricing.feat.notifications': 'Notifications',
  'pricing.feat.guide': 'Guide numérique',
  'pricing.feat.rewards': 'Récompenses & Colocataires',
  'pricing.feat.endlessRecords': 'Enregistrements illimités',
  'pricing.feat.vault50': 'Coffre-fort (jusquà 50 fichiers)',
  'pricing.feat.vault150': 'Coffre-fort (150 fichiers)',
  'pricing.feat.vault5': 'Vet Records Vault (5 files)',
  'pricing.feat.customTrends': 'Customisable Extended Trends',
  'pricing.feat.trends': 'Trends & Charts',
  'pricing.feat.digest': 'Digest mensuel de néphrologie',
  'pricing.feat.onePet': '1 Pet Profile',
  'pricing.feat.vitalPets': 'Up to 4 Pet Profiles',
  'pricing.feat.coCarers': '2 co-carers par animal',
  'pricing.feat.fellowship': 'The Koda Fellowship',
  'pricing.feat.unlimitedPets': "Jusqu'à 30 profils d'animaux",
  'pricing.feat.vetExport': 'Vet-Ready Data Exports',
  'blog.heading': "De l'équipe KodaCare",
  'blog.subheading': 'Actualités et témoignages de notre communauté.',
  'blog.readMore': 'Lire la suite →',
  'blog.backToBlog': 'Retour au blog',
  'blog.minRead': 'min de lecture',
  'blog.moreFromTeam': "Plus de l'équipe KodaCare",
  'blog.readAllArticles': 'Lire tous les articles',
  'blog.tag.newNorms': 'Nouvelles normes',
  'blog.tag.roomies': 'Roomies',
  'blog.tag.labs': 'Analyses',
  'blog.tag.routines': 'Routines',
  'blog.authorTeam': 'L’équipe KodaCare',
  'legal.eyebrow': 'Juridique',
  'legal.lastUpdated': 'Dernière mise à jour',
  'legal.privacyTitle': 'Politique de confidentialité',
  'legal.termsTitle': 'Conditions d’utilisation',
  'faq.heading': 'Questions fréquemment posées',
  'faq.subheading': 'Tout ce que vous devez savoir sur KodaCare.',
  'faq.stillHaveQuestions': 'Vous avez d\'autres questions ?',
  'faq.contactUs': 'Notre équipe d\'assistance est là pour vous aider.',
  'faq.emailSupport': 'Contacter le support',
  'faq.category.gettingStarted': 'Premiers pas',
  'faq.category.vitals': 'Journal des constantes',
  'faq.category.meds': 'Centre médicaments',
  'faq.category.guide': 'Guide numérique',
  'faq.category.notifications': 'Notifications et récompenses',
  'faq.category.roomies': 'Colocataires et communauté',
  'faq.category.trends': 'Tendances et données',
  'faq.category.security': 'Confidentialité et sécurité',
  'faq.category.pricing': 'Prix et fonctionnalités',
  'faq.category.technical': 'Support technique',
  'faq.q.whatIsKodaCare': 'Qu\'est-ce que KodaCare ?',
  'faq.a.whatIsKodaCare': 'KodaCare est un compagnon numérique de santé animale pour les pet parents modernes.',
  'faq.q.whoCanUse': 'Qui peut utiliser KodaCare ?',
  'faq.a.whoCanUse': 'KodaCare est conçu pour les pet parents, les proches et les foyers qui gèrent la santé quotidienne d’un compagnon.',
  'faq.q.howToSignUp': 'Comment m\'inscrire ?',
  'faq.a.howToSignUp': 'Visitez l\'application ou le site KodaCare et appuyez sur "S\'inscrire".',
  'faq.q.techSkills': 'Dois-je être expert en technologie ?',
  'faq.a.techSkills': 'Non du tout ! KodaCare est conçu pour être intuitif et facile à utiliser pour le suivi de votre animal.',
  'faq.q.whatAreVitals': 'Que sont les signes vitaux et pourquoi les enregistrer ?',
  'faq.a.whatAreVitals': 'Les signes vitaux sont des mesures des fonctions physiques de base de votre corps.',
  'faq.q.howOftenLog': 'À quelle fréquence dois-je enregistrer mes constantes ?',
  'faq.a.howOftenLog': 'Votre équipe de greffe vous donnera des instructions spécifiques.',
  'faq.q.normalRanges': 'Quelles sont les plages normales pour les signes vitaux ?',
  'faq.a.normalRanges': 'Les plages normales varient selon les individus.',
  'faq.q.fluidIntake': 'Pourquoi l\'application demande-t-elle l\'apport liquidien ?',
  'faq.a.fluidIntake': 'Les patients post-greffe doivent surveiller attentivement l\'apport liquidien.',
  'faq.q.editDeleteLogs': 'Puis-je modifier ou supprimer une entrée ?',
  'faq.a.editDeleteLogs': 'Oui ! Vous pouvez modifier ou supprimer n\'importe quelle entrée.',
  'faq.q.addMedication': 'Comment ajouter mes médicaments ?',
  'faq.a.addMedication': 'Allez dans la section Médicaments et appuyez sur "Ajouter un médicament".',
  'faq.q.immunosuppressants': 'Que sont les immunosuppresseurs ?',
  'faq.a.immunosuppressants': 'Les immunosuppresseurs empêchent votre système immunitaire de rejeter votre nouveau rein.',
  'faq.q.reminders': 'Comment configurer les rappels de médicaments ?',
  'faq.a.reminders': 'Lorsque vous ajoutez un médicament, vous pouvez définir l\'heure du rappel.',
  'faq.q.missedDose': 'Que faire si je manque une dose ?',
  'faq.a.missedDose': 'Enregistrez-la dans l\'application et contactez votre équipe de greffe immédiatement.',
  'faq.q.multipleUsers': 'Un appareil peut-il être utilisé par plusieurs patients ?',
  'faq.a.multipleUsers': 'Chaque compte est lié aux données de santé d\'une seule personne.',
  'faq.q.whatInGuide': 'Quelles informations sont dans le guide numérique ?',
  'faq.a.whatInGuide': 'Le guide numérique couvre cinq sujets essentiels : alimentation, hygiène, exercice, médicaments et obstacles.',
  'faq.q.languages': 'Quelles langues l\'application supporte-t-elle ?',
  'faq.a.languages': 'KodaCare est disponible en 5 langues : anglais, français, espagnol, allemand et portugais.',
  'faq.q.guideAccuracy': 'Quelle est la pertinence des informations du guide ?',
  'faq.a.guideAccuracy': 'Le guide est créé avec l\'apport de cliniciens de transplantation.',
  'faq.q.updateGuide': 'Le guide est-il mis à jour ?',
  'faq.a.updateGuide': 'Oui, nous mettons à jour régulièrement le guide numérique.',
  'faq.q.notificationTypes': 'Quel type de notifications vais-je recevoir ?',
  'faq.a.notificationTypes': 'KodaCare envoie des rappels de soins, de médicaments et d\'enregistrement.',
  'faq.q.customizeNotifications': 'Puis-je désactiver les notifications ?',
  'faq.a.customizeNotifications': 'Absolument. Allez dans Paramètres et personnalisez les notifications.',
  'faq.q.twoNotifications': 'Pourquoi est-ce que je reçois deux notifications en même temps ?',
  'faq.a.twoNotifications': 'C\'est tout à fait normal lors de la première configuration de KodaCare. Votre appareil envoie deux demandes de permission distinctes — l\'une depuis le navigateur, l\'autre depuis l\'application. Si vous avez accepté les deux, vous pourrez recevoir des notifications en double pendant un court moment. Elles se synchroniseront rapidement. Si les doublons persistent, allez dans Paramètres → Notifications, désactivez-les puis réactivez-les.',
  'faq.q.whatIsKoda': 'Qu\'est-ce que Koda ?',
  'faq.a.whatIsKoda': 'Koda est le système de récompenses de KodaCare pour vous motiver.',
  'faq.q.earnXP': 'Comment gagner des XP ?',
  'faq.a.earnXP': 'Vous gagnez des XP en enregistrant vos constantes, vos médicaments et en lisant des articles.',
  'faq.q.streaks': 'Qu\'est-ce qu\'une séquence ?',
  'faq.a.streaks': 'Une séquence est un décompte continu de jours où vous avez enregistré vos constantes.',
  'faq.q.whatAreRoomies': 'Que sont les colocataires ?',
  'faq.a.whatAreRoomies': 'Les colocataires sont d\'autres recipients de transplantation que vous connaissez.',
  'faq.q.joinRoomies': 'Comment rejoindre un groupe de colocataires ?',
  'faq.a.joinRoomies': 'Allez dans Paramètres → Colocataires et entrez un code de groupe.',
  'faq.q.privacy': 'Mes données sont-elles privées ?',
  'faq.a.privacy': 'Oui. Vous devez explicitement activer les colocataires pour partager les données.',
  'faq.q.leaveRoomies': 'Puis-je quitter un groupe de colocataires ?',
  'faq.a.leaveRoomies': 'Oui. Vous pouvez désactiver les colocataires à tout moment.',
  'faq.q.whatAreTrends': 'Que sont les tendances ?',
  'faq.a.whatAreTrends': 'Les tendances transforment vos données en graphiques faciles à lire.',
  'faq.q.shareData': 'Puis-je partager mes données avec mon équipe ?',
  'faq.a.shareData': 'Oui. Vous pouvez exporter votre rapport de tendances directement depuis l\'application.',
  'faq.q.dataDownload': 'Puis-je télécharger toutes mes données de santé ?',
  'faq.a.dataDownload': 'Oui, vous pouvez télécharger une copie de toutes vos données enregistrées.',
  'faq.q.dataEncrypted': 'Mes données sont-elles chiffrées ?',
  'faq.a.dataEncrypted': 'Oui. KodaCare utilise le chiffrement standard de l\'industrie pour protéger les données de votre animal.',
  'faq.q.whoAccess': 'Qui peut accéder à mes informations de santé ?',
  'faq.a.whoAccess': 'Seul vous pouvez accéder à vos données complètes.',
  'faq.q.compliance': 'KodaCare est-il conforme aux lois sur la confidentialité ?',
  'faq.a.compliance': 'Oui. KodaCare est conforme au RGPD (UE) et au UK GDPR.',
  'faq.q.deleteAccount': 'Puis-je supprimer mon compte et mes données ?',
  'faq.a.deleteAccount': 'Oui. Allez dans Paramètres → Compte et sélectionnez "Supprimer le compte".',
  'faq.q.dataRetention': 'Combien de temps mes données sont-elles conservées ?',
  'faq.a.dataRetention': 'Les données de compte actif sont conservées pendant la durée de votre compte. En cas de suppression, vos données personnelles sont effacées sous 30 jours. Des enregistrements agrégés anonymisés peuvent être conservés jusqu\'à 6 mois pour les obligations de conformité des dispositifs médicaux. Consultez notre Politique de confidentialité pour plus de détails.',
  'faq.q.freePlan': 'Combien de temps puis-je utiliser KodaCare gratuitement ?',
  'faq.a.freePlan': 'Le plan gratuit inclut un accès complet pendant vos 15 premiers jours.',
  'faq.q.planDifference': 'Quelle est la différence entre les plans ?',
  'faq.a.planDifference': 'Le Compagnon Vital offre un stockage de 1 an, tandis que le Compagnon Vitaliciel offre un stockage illimité.',
  'faq.q.upgrade': 'Comment mettre à niveau mon plan ?',
  'faq.a.upgrade': 'Après votre essai gratuit, l\'application vous demandera de choisir un plan.',
  'faq.q.refund': 'Quelle est votre politique de remboursement ?',
  'faq.a.refund': 'Si vous n\'êtes pas satisfait dans les 30 jours, nous offrons un remboursement complet.',
  'faq.q.devices': 'Sur quels appareils puis-je utiliser KodaCare ?',
  'faq.a.devices': 'KodaCare fonctionne sur iPhone, téléphones Android, iPad et navigateurs Web.',
  'faq.q.offline': 'Puis-je utiliser KodaCare hors ligne ?',
  'faq.a.offline': 'Oui ! L\'application stocke les données localement sur votre appareil.',
  'faq.q.syncData': 'Comment les données se synchronisent-elles entre mes appareils ?',
  'faq.a.syncData': 'Lorsque vous vous connectez sur plusieurs appareils, KodaCare synchronise vos données en temps réel.',
  'faq.q.support': 'Comment obtenir de l\'aide ?',
  'faq.a.support': 'Utilisez la section Aide dans les Paramètres de l\'application. Notre équipe répond généralement sous 24 heures.',
  'personas.eyebrow': 'DESIGNED FOR EVERY COMPANION',
  'personas.heading': 'Designed for Every Companion',
  'personas.subheading': 'KodaCare adapts to your life, whether you have one pup or a whole pasture.',
  'personas.modern.title': 'The Modern Pet Parent',
  'personas.modern.tag': '"Peace of Mind"',
  'personas.modern.desc': 'Gamified habits that turn daily care into a win. Perfect for tracking streaks and sharing milestones in the Fellowship.',
  'personas.elderly.title': 'The Elderly Dog Carer',
  'personas.elderly.tag': '"The Safety Net"',
  'personas.elderly.desc': 'Simple, bold visuals and multi-user alerts so the whole family can support the care of a beloved senior pet.',
  'personas.farm.title': 'The Modern Farm Keeper',
  'personas.farm.tag': '"The Herd Hub"',
  'personas.farm.desc': 'Professional-grade data management for multiple companions. Track herd health trends and export professional reports for your vet.',
  'footer.tagline': 'Le hub numérique pour la récupération post-greffe.',
  'footer.disclaimer':
    "KodaCare est un outil de suivi de santé animale uniquement. Il ne fournit pas de conseils vétérinaires.",
  'footer.product': 'Produit',
  'footer.account': 'Compte',
  'footer.legal': 'Légal',
  'footer.terms': 'Conditions générales',
  'footer.privacy': 'Politique de confidentialité',
  'footer.faq': 'FAQ',
  'footer.rightsReserved': 'Tous droits réservés.',
  'gdpr.title': 'Nous utilisons des cookies',
  'gdpr.body': "Nous utilisons des cookies essentiels pour maintenir votre connexion et mémoriser vos préférences. Des cookies analytiques nous aident à améliorer KodaCare. Aucune donnée de santé animale personnelle n'est partagée avec des annonceurs tiers.",
  'gdpr.learnMore': 'En savoir plus dans notre Politique de confidentialité',
  'gdpr.accept': 'Tout accepter',
  'gdpr.decline': 'Essentiels uniquement',
};

const de: WebsiteTranslations = {
  'site.titleSuffix': 'Gesundheitsbegleiter nach Nierentransplantation',
  'site.description':
    'KodaCare hilft Tierhalterinnen und Tierhaltern dabei, Medikamente, Trinkmenge und tägliche Gesundheitswerte ihres Tieres zu dokumentieren – mit praktischen Hinweisen und übersichtlichen Dashboards.',
  'nav.features': 'FUNKTIONEN',
  'nav.pricing': 'PREISE',
  'nav.stories': 'GESCHICHTEN',
  'nav.login': 'Anmelden',
  'nav.signup': 'Registrieren',
  'nav.blog': 'Blog',
  'hero.eyebrow': 'PATIENTEN-HUB NACH TRANSPLANTATION',
  'hero.headline0': '',
  'hero.headline1': 'Genesung ist',
  'hero.headline2': 'ein Marathon.',
  'hero.headline3': 'Laufe ihn nicht',
  'hero.headline4': 'ohne KodaCare.',
  'hero.body':
    'Umfassende Tierpflege, vereinfacht. KodaCare hilft Ihnen, Medikamente und Gesundheitstrends für jedes Haustier im Blick zu behalten. Bleiben Sie mühelos auf Kurs und erklimmen Sie die Ränge unserer tierliebenden Fellowship.',
  'hero.cta': 'Kostenlos testen',
  'hero.cta.openApp': 'App öffnen',
  'hero.cta.installOn': 'Installieren auf',
  'hero.cta.installApp': 'App installieren',
  'hero.installTip.ios': "Tippen Sie in Safari auf Teilen und dann auf „Zum Home-Bildschirm“.",
  'hero.installTip.android': "Öffnen Sie das Browser-Menü und wählen Sie „Zum Startbildschirm hinzufügen“ oder „App installieren“.",
  'hero.installTip.default': "Suchen Sie in der Adressleiste Ihres Browsers nach dem Installationssymbol (⊕) und klicken Sie darauf.",
  'install.modal.title': 'KodaCare zu Ihrem Gerät hinzufügen',
  'install.modal.subtitle': 'Erleben Sie die vollständige App:',
  'install.modal.android.cta': 'Installieren',
  'install.modal.feature1': 'Offline-Zugriff',
  'install.modal.feature2': 'Push-Benachrichtigungen',
  'install.modal.feature3': 'Sofortstart',
  'install.modal.android.hint': 'Tippen Sie auf die Schaltfläche oben und bestätigen Sie in der erscheinenden Eingabeaufforderung.',
  'install.modal.android.step1': 'Tippen Sie auf das Browsermenü (⋮)',
  'install.modal.android.step2': "Tippen Sie auf 'Zum Startbildschirm hinzufügen'",
  'install.modal.ios.step1': 'Tippen Sie auf die Teilen-Schaltfläche unten auf dem Bildschirm',
  'install.modal.ios.step2': "Scrollen Sie nach unten und tippen Sie auf 'Zum Home-Bildschirm'",
  'install.modal.ios.step3': "Tippen Sie oben rechts auf 'Hinzufügen' zur Bestätigung",
  'install.modal.ios.hint': 'Verwenden Sie Safari für die beste Installationserfahrung auf iOS.',
  'install.modal.desktop.tip': 'Suchen Sie in der Adressleiste nach dem Installationssymbol (ein Bildschirm mit Downloadpfeil) und klicken Sie darauf, um KodaCare zu Ihrem Desktop hinzuzufügen.',
  'install.modal.dismiss': 'Vielleicht später',
  'features.eyebrow': 'ALLES, WAS SIE BRAUCHEN',
  'features.heading': 'Für das Leben nach der Transplantation',
  'features.subheading':
    'KodaCare vereint alle Werkzeuge, die Sie brauchen, um die Pflegeroutine Ihres Tieres zu verwalten.',
  'features.vitals.title': 'Vitalwerte-Logbuch',
  'features.vitals.desc':
    'Blutdruck, Gewicht, Temperatur, Flüssigkeitsaufnahme und Urinausscheidung zweimal täglich erfassen.',
  'features.meds.title': 'Medikamentenzentrum',
  'features.meds.desc': 'Behalten Sie den Überblick über jedes Medikament mit visuellen Formen, Dosierungen und Tageszeitfenstern. Smarte Erinnerungen verhindern Verwechslungen und Doppeldosierungen.',
  'features.guide.title': 'Digitaler Leitfaden',
  'features.guide.desc':
    'Ein umfassendes Handbuch nach der Transplantation — in 5 Sprachen.',
  'features.notifications.title': 'Intelligente Benachrichtigungen',
  'features.notifications.desc':
    'Rechtzeitige Hinweise für Ihre Vitalwerte und Ihren Medikamentenplan.',
  'features.trends.title': 'Trends & Einblicke',
  'features.trends.desc': 'Schöne Diagramme verwandeln Ihre Rohdaten in aussagekräftige Trends.',
  'features.rewards.title': 'Belohnungen & Mitbewohner',
  'features.rewards.desc': 'Bleiben Sie motiviert mit Streak-Belohnungen.',
  'pricing.heading': 'Pläne & Preise',
  'pricing.subheading': 'Starten Sie 15 Tage kostenlos. Upgraden Sie, um Ihre Daten zu behalten und unbegrenzte Einträge zu nutzen.',
  'pricing.monthly': 'MONATLICH',
  'pricing.yearly': 'JÄHRLICH',
  'pricing.mostPopular': 'AM BELIEBTESTEN',
  'pricing.perMonth': '/Monat',
  'pricing.choosePlan': 'Plan wählen',
  'pricing.startFree': 'Kostenlos starten',
  'pricing.currentPlan': 'Ihr aktueller Plan',
  'pricing.free.name': 'Starter',
  'pricing.free.price': 'KOSTENLOS',
  'pricing.free.subtitle': '15-tägige Testphase — alle Funktionen inklusive. Nach 15 Tagen upgraden, um Ihre Daten zu behalten.',
  'pricing.vital.name': 'Vital-Begleiter',
  'pricing.vital.subtitle': 'Unbegrenzte Einträge, Dokumentenspeicher und Vollzugang — alles, was Sie langfristig brauchen.',
  'pricing.lifetime.name': 'Expert Companion',
  'pricing.lifetime.subtitle': 'Alles aus Vital, plus anpassbare Trendgrafiken, ein größerer Dokumententresor und ein monatlicher Überblick über aktuelle Entwicklungen in der Nephrologie.',
  'pricing.feat.vitals': 'Vitalwerte-Logbuch',
  'pricing.feat.meds': 'Medikamentenzentrum',
  'pricing.feat.notifications': 'Benachrichtigungen',
  'pricing.feat.guide': 'Digitaler Leitfaden',
  'pricing.feat.rewards': 'Belohnungen & Mitbewohner',
  'pricing.feat.endlessRecords': 'Unbegrenzte Einträge',
  'pricing.feat.vault50': 'Dokumententresor (bis zu 50 Dateien)',
  'pricing.feat.vault150': 'Dokumententresor (150 Dateien)',
  'pricing.feat.vault5': 'Vet Records Vault (5 files)',
  'pricing.feat.customTrends': 'Customisable Extended Trends',
  'pricing.feat.trends': 'Trends & Charts',
  'pricing.feat.digest': 'Monatlicher Nephrologie-Digest',
  'pricing.feat.onePet': '1 Pet Profile',
  'pricing.feat.vitalPets': 'Up to 4 Pet Profiles',
  'pricing.feat.coCarers': '2 Co-Carer pro Tier',
  'pricing.feat.fellowship': 'The Koda Fellowship',
  'pricing.feat.unlimitedPets': 'Bis zu 30 Tierprofilen',
  'pricing.feat.vetExport': 'Vet-Ready Data Exports',
  'blog.heading': 'Vom KodaCare-Team',
  'blog.subheading': 'Einblicke, Updates und Geschichten aus unserer Gemeinschaft.',
  'blog.readMore': 'Mehr lesen →',
  'blog.backToBlog': 'Zurück zum Blog',
  'blog.minRead': 'Min. Lesezeit',
  'blog.moreFromTeam': 'Mehr vom KodaCare-Team',
  'blog.readAllArticles': 'Alle Artikel lesen',
  'blog.tag.newNorms': 'Neue Normen',
  'blog.tag.roomies': 'Roomies',
  'blog.tag.labs': 'Laborwerte',
  'blog.tag.routines': 'Routinen',
  'blog.authorTeam': 'Das KodaCare-Team',
  'legal.eyebrow': 'Rechtliches',
  'legal.lastUpdated': 'Zuletzt aktualisiert',
  'legal.privacyTitle': 'Datenschutzerklärung',
  'legal.termsTitle': 'Nutzungsbedingungen',
  'faq.heading': 'Häufig gestellte Fragen',
  'faq.subheading': 'Alles, was Sie über KodaCare wissen müssen.',
  'faq.stillHaveQuestions': 'Haben Sie noch Fragen?',
  'faq.contactUs': 'Unser Support-Team ist hier, um Ihnen zu helfen.',
  'faq.emailSupport': 'Support kontaktieren',
  'faq.category.gettingStarted': 'Erste Schritte',
  'faq.category.vitals': 'Vitalwerte-Logbuch',
  'faq.category.meds': 'Medikamentenzentrum',
  'faq.category.guide': 'Digitaler Leitfaden',
  'faq.category.notifications': 'Benachrichtigungen & Belohnungen',
  'faq.category.roomies': 'Roomies & Community',
  'faq.category.trends': 'Trends & Daten',
  'faq.category.security': 'Datenschutz & Sicherheit',
  'faq.category.pricing': 'Preise & Funktionen',
  'faq.category.technical': 'Technischer Support',
  'faq.q.whatIsKodaCare': 'Was ist KodaCare?',
  'faq.a.whatIsKodaCare': 'KodaCare ist ein digitaler Gesundheitsbegleiter für Tierhalterinnen und Tierhalter.',
  'faq.q.whoCanUse': 'Wer kann KodaCare nutzen?',
  'faq.a.whoCanUse': 'KodaCare ist für Tierhalterinnen, Tierhalter und Familien entwickelt, die die Gesundheit ihres Tieres gemeinsam organisieren.',
  'faq.q.howToSignUp': 'Wie melde ich mich an?',
  'faq.a.howToSignUp': 'Öffnen Sie App oder Website, tippen Sie auf "Registrieren", bestätigen Sie Ihre E-Mail und vervollständigen Sie Ihr Profil.',
  'faq.q.techSkills': 'Muss ich technikaffin sein?',
  'faq.a.techSkills': 'Nein. KodaCare ist bewusst einfach aufgebaut und führt Sie Schritt für Schritt durch die wichtigsten Bereiche der Tierpflege.',
  'faq.q.whatAreVitals': 'Was sind Vitalwerte und warum soll ich sie protokollieren?',
  'faq.a.whatAreVitals': 'Vitalwerte sind Basisdaten wie Blutdruck, Gewicht, Temperatur, Trinkmenge und Urinausfuhr. Regelmäßige Einträge helfen Ihrem Team, Veränderungen früh zu erkennen.',
  'faq.q.howOftenLog': 'Wie oft soll ich Vitalwerte erfassen?',
  'faq.a.howOftenLog': 'In der Regel morgens und abends. Folgen Sie immer den individuellen Vorgaben Ihres Transplantationsteams.',
  'faq.q.normalRanges': 'Welche Werte gelten als normal?',
  'faq.a.normalRanges': 'Das ist individuell verschieden. Maßgeblich sind die Zielbereiche, die Ihr medizinisches Team für Sie festlegt.',
  'faq.q.fluidIntake': 'Warum wird die Trinkmenge erfasst?',
  'faq.a.fluidIntake': 'Nach einer Transplantation ist Flüssigkeitsmanagement zentral. Die Dokumentation unterstützt Sie und Ihr Team bei der Verlaufskontrolle.',
  'faq.q.editDeleteLogs': 'Kann ich Einträge bearbeiten oder löschen?',
  'faq.a.editDeleteLogs': 'Ja. In der Records-Ansicht können Sie Einträge jederzeit korrigieren oder entfernen.',
  'faq.q.addMedication': 'Wie füge ich Medikamente hinzu?',
  'faq.a.addMedication': 'Öffnen Sie den Bereich Meds, tippen Sie auf "Add Medication" und tragen Sie Name, Dosis und Zeiten ein.',
  'faq.q.immunosuppressants': 'Warum sind Immunsuppressiva so wichtig?',
  'faq.a.immunosuppressants': 'Sie verhindern die Abstoßung des transplantierten Organs. Schon einzelne ausgelassene Dosen können riskant sein.',
  'faq.q.reminders': 'Wie stelle ich Erinnerungen ein?',
  'faq.a.reminders': 'Beim Anlegen eines Medikaments können Sie individuelle Erinnerungszeiten festlegen.',
  'faq.q.missedDose': 'Was mache ich bei einer verpassten Dosis?',
  'faq.a.missedDose': 'Dokumentieren Sie den Vorfall und kontaktieren Sie Ihr Transplantationsteam zeitnah für die richtige Handlungsempfehlung.',
  'faq.q.multipleUsers': 'Kann ein Gerät von mehreren Personen genutzt werden?',
  'faq.a.multipleUsers': 'Ja, aber jede Person benötigt ein eigenes Konto, damit Daten getrennt und korrekt bleiben.',
  'faq.q.whatInGuide': 'Was enthält der digitale Leitfaden?',
  'faq.a.whatInGuide': 'Der Leitfaden deckt Ernährung, Hygiene, Bewegung, Medikamente und typische Hürden im Alltag ab.',
  'faq.q.languages': 'Welche Sprachen werden unterstützt?',
  'faq.a.languages': 'Aktuell: Englisch, Französisch, Deutsch, Portugiesisch und Spanisch.',
  'faq.q.guideAccuracy': 'Wie aktuell sind die Inhalte?',
  'faq.a.guideAccuracy': 'Die Inhalte werden regelmäßig überprüft und mit klinischem Input aktualisiert.',
  'faq.q.updateGuide': 'Wird der Leitfaden aktualisiert?',
  'faq.a.updateGuide': 'Ja, neue Inhalte und Verbesserungen werden fortlaufend in die App eingespielt.',
  'faq.q.notificationTypes': 'Welche Benachrichtigungen gibt es?',
  'faq.a.notificationTypes': 'Vor allem Medikamenten-Erinnerungen und Hinweise für Vitalwert-Einträge.',
  'faq.q.customizeNotifications': 'Kann ich Benachrichtigungen anpassen?',
  'faq.a.customizeNotifications': 'Ja. In den Einstellungen können Sie sie pro Typ aktivieren, deaktivieren oder anpassen.',
  'faq.q.twoNotifications': 'Warum erhalte ich zwei Benachrichtigungen gleichzeitig?',
  'faq.a.twoNotifications': 'Das ist bei der Ersteinrichtung von KodaCare völlig normal. Ihr Gerät sendet zwei separate Berechtigungsanfragen — eine vom Browser und eine von der App selbst. Wenn Sie beiden zugestimmt haben, können kurzzeitig doppelte Benachrichtigungen erscheinen. Das reguliert sich von selbst. Wenn die Duplikate anhalten, gehen Sie zu Einstellungen → Benachrichtigungen, deaktivieren und reaktivieren Sie sie.',
  'faq.q.whatIsKoda': 'Was ist Koda?',
  'faq.a.whatIsKoda': 'Koda ist das Motivationssystem von KodaCare. Es belohnt konstante Gewohnheiten mit XP und Fortschritt.',
  'faq.q.earnXP': 'Wie sammle ich XP?',
  'faq.a.earnXP': 'Durch tägliche Einträge, Medikamenten-Logs, Guide-Artikel und Streak-Meilensteine.',
  'faq.q.streaks': 'Was ist eine Streak?',
  'faq.a.streaks': 'Eine Streak zählt aufeinanderfolgende Tage mit Aktivität und stärkt Ihre Routine.',
  'faq.q.whatAreRoomies': 'Was sind Roomies?',
  'faq.a.whatAreRoomies': 'Roomies sind andere Transplantierte, mit denen Sie sich auf freiwilliger Basis verbinden können.',
  'faq.q.joinRoomies': 'Wie trete ich einer Roomies-Gruppe bei?',
  'faq.a.joinRoomies': 'Unter Einstellungen → Roomies können Sie einen Gruppencode erstellen oder beitreten.',
  'faq.q.privacy': 'Sind meine Daten in Roomies privat?',
  'faq.a.privacy': 'Ja. Es werden nur begrenzte Aktivitätssignale geteilt, niemals Ihre kompletten Messwerte.',
  'faq.q.leaveRoomies': 'Kann ich Roomies wieder verlassen?',
  'faq.a.leaveRoomies': 'Ja, Sie können Roomies jederzeit deaktivieren oder die Gruppe wechseln.',
  'faq.q.whatAreTrends': 'Was sind Trends?',
  'faq.a.whatAreTrends': 'Trends visualisieren Ihre Daten in Diagrammen, damit Muster im Verlauf leichter erkennbar sind.',
  'faq.q.shareData': 'Kann ich Daten mit Ärztinnen/Ärzten teilen?',
  'faq.a.shareData': 'Ja, Sie können Berichte exportieren und vor Terminen gezielt weitergeben.',
  'faq.q.dataDownload': 'Kann ich alle Daten herunterladen?',
  'faq.a.dataDownload': 'Ja, in den Einstellungen können Sie einen Export Ihrer Daten herunterladen.',
  'faq.q.dataEncrypted': 'Sind meine Daten verschlüsselt?',
  'faq.a.dataEncrypted': 'Ja, KodaCare nutzt gängige Verschlüsselungsstandards für Übertragung und Speicherung.',
  'faq.q.whoAccess': 'Wer hat Zugriff auf meine Daten?',
  'faq.a.whoAccess': 'Vollzugriff haben nur Sie, außer Sie teilen Informationen aktiv und ausdrücklich.',
  'faq.q.compliance': 'Ist KodaCare datenschutzkonform?',
  'faq.a.compliance': 'Ja, die Plattform orientiert sich an relevanten Datenschutzstandards wie DSGVO und HIPAA.',
  'faq.q.deleteAccount': 'Kann ich Konto und Daten löschen?',
  'faq.a.deleteAccount': 'Ja, über die Kontoeinstellungen können Sie die dauerhafte Löschung veranlassen.',
  'faq.q.dataRetention': 'Wie lange werden meine Daten gespeichert?',
  'faq.a.dataRetention': 'Aktive Kontodaten werden für die Lebensdauer Ihres Kontos gespeichert. Nach einer Löschanfrage werden Ihre personenbezogenen Daten innerhalb von 30 Tagen entfernt. Anonymisierte aggregierte Daten können bis zu 6 Monate für Medizinprodukt-Compliance aufbewahrt werden. Weitere Details finden Sie in unserer Datenschutzerklärung.',
  'faq.q.freePlan': 'Wie lange ist die kostenlose Nutzung?',
  'faq.a.freePlan': 'Der Starter-Plan bietet 15 Tage Zugriff auf die Kernfunktionen.',
  'faq.q.planDifference': 'Was unterscheidet die Bezahlpläne?',
  'faq.a.planDifference': 'Vital Companion bietet erweitertes Logging mit Speicherzeitraum; Expert Companion enthält langfristige Archivierung und Familienfunktionen.',
  'faq.q.upgrade': 'Wie upgrade ich meinen Plan?',
  'faq.a.upgrade': 'Nach der Testphase oder jederzeit in den Einstellungen unter Subscription.',
  'faq.q.refund': 'Wie funktioniert die Rückerstattung?',
  'faq.a.refund': 'Bei Anfragen innerhalb von 30 Tagen prüfen wir eine vollständige Rückerstattung.',
  'faq.q.devices': 'Auf welchen Geräten läuft KodaCare?',
  'faq.a.devices': 'Auf iPhone, Android, iPad und im Webbrowser mit demselben Konto.',
  'faq.q.offline': 'Kann ich die App offline nutzen?',
  'faq.a.offline': 'Ja, Einträge werden lokal gespeichert und bei Verbindung automatisch synchronisiert.',
  'faq.q.syncData': 'Wie funktioniert die Synchronisierung?',
  'faq.a.syncData': 'Daten werden geräteübergreifend in Echtzeit abgeglichen, sobald eine Verbindung besteht.',
  'faq.q.support': 'Wie erhalte ich Hilfe?',
  'faq.a.support': 'Verwenden Sie den Hilfe-Bereich in den App-Einstellungen. Unser Team antwortet in der Regel innerhalb von 24 Stunden.',
  'personas.eyebrow': 'DESIGNED FOR EVERY COMPANION',
  'personas.heading': 'Designed for Every Companion',
  'personas.subheading': 'KodaCare adapts to your life, whether you have one pup or a whole pasture.',
  'personas.modern.title': 'The Modern Pet Parent',
  'personas.modern.tag': '"Peace of Mind"',
  'personas.modern.desc': 'Gamified habits that turn daily care into a win. Perfect for tracking streaks and sharing milestones in the Fellowship.',
  'personas.elderly.title': 'The Elderly Dog Carer',
  'personas.elderly.tag': '"The Safety Net"',
  'personas.elderly.desc': 'Simple, bold visuals and multi-user alerts so the whole family can support the care of a beloved senior pet.',
  'personas.farm.title': 'The Modern Farm Keeper',
  'personas.farm.tag': '"The Herd Hub"',
  'personas.farm.desc': 'Professional-grade data management for multiple companions. Track herd health trends and export professional reports for your vet.',
  'footer.tagline': 'Der digitale Hub für die Genesung nach der Transplantation.',
  'footer.disclaimer':
    'KodaCare ist nur ein Tool zur Tiergesundheits-Dokumentation. Es bietet keine tierärztliche Beratung.',
  'footer.product': 'Produkt',
  'footer.account': 'Konto',
  'footer.legal': 'Rechtliches',
  'footer.terms': 'AGB',
  'footer.privacy': 'Datenschutzerklärung',
  'footer.faq': 'FAQ',
  'footer.rightsReserved': 'Alle Rechte vorbehalten.',
  'gdpr.title': 'Wir verwenden Cookies',
  'gdpr.body': 'Wir verwenden notwendige Cookies, um Sie angemeldet zu halten und Ihre Einstellungen zu speichern. Analyse-Cookies helfen uns, KodaCare zu verbessern. Persönliche Tiergesundheitsdaten werden niemals mit Dritten zu Werbezwecken geteilt.',
  'gdpr.learnMore': 'Mehr in unserer Datenschutzerklärung',
  'gdpr.accept': 'Alle akzeptieren',
  'gdpr.decline': 'Nur notwendige',
};

const pt: WebsiteTranslations = {
  'site.titleSuffix': 'Companheiro de saúde para transplante renal',
  'site.description':
    'A KodaCare ajuda tutores de animais a monitorizar medicação, ingestão de água e saúde diária — com orientação prática e painéis claros.',
  'nav.features': 'FUNCIONALIDADES',
  'nav.pricing': 'PREÇOS',
  'nav.stories': 'HISTÓRIAS',
  'nav.login': 'Entrar',
  'nav.signup': 'Registar',
  'nav.blog': 'Blog',
  'hero.eyebrow': 'HUB DO PACIENTE PÓS-TRANSPLANTE',
  'hero.headline0': '',
  'hero.headline1': 'A recuperação é',
  'hero.headline2': 'uma maratona.',
  'hero.headline3': 'Não a corra',
  'hero.headline4': 'sem KodaCare.',
  'hero.body':
    'Cuidado completo do seu animal, simplificado. O KodaCare ajuda-o a acompanhar medicamentos e tendências de saúde de todos os animais em casa. Mantenha-se no caminho certo e suba nas fileiras da nossa Fellowship apaixonada por animais.',
  'hero.cta': 'Experimentar gratuitamente',
  'hero.cta.openApp': 'Abrir a app',
  'hero.cta.installOn': 'Instalar no',
  'hero.cta.installApp': 'Instalar a app',
  'hero.installTip.ios': "Toque em Partilhar no Safari e depois escolha «Adicionar ao ecrã principal».",
  'hero.installTip.android': "Abra o menu do navegador e escolha «Adicionar ao ecrã principal» ou «Instalar aplicação».",
  'hero.installTip.default': "Procure o ícone de instalação (⊕) na barra de endereço do navegador e clique para instalar.",
  'install.modal.title': 'Adicionar KodaCare ao seu dispositivo',
  'install.modal.subtitle': 'Tenha a experiência completa da app:',
  'install.modal.android.cta': 'Instalar',
  'install.modal.feature1': 'Acesso offline',
  'install.modal.feature2': 'Notificações push',
  'install.modal.feature3': 'Lançamento instantâneo',
  'install.modal.android.hint': 'Toque no botão acima e confirme na janela que aparecer.',
  'install.modal.android.step1': 'Toque no menu do navegador (⋮)',
  'install.modal.android.step2': "Toque em 'Adicionar ao ecrã inicial'",
  'install.modal.ios.step1': 'Toque no botão Partilhar na parte inferior do ecrã',
  'install.modal.ios.step2': "Deslize para baixo e toque em 'Adicionar ao ecrã principal'",
  'install.modal.ios.step3': "Toque em 'Adicionar' no canto superior direito para confirmar",
  'install.modal.ios.hint': 'Use o Safari para a melhor experiência de instalação no iOS.',
  'install.modal.desktop.tip': 'Procure o ícone de instalação na barra de endereço (parece um ecrã com uma seta de transferência) e clique para adicionar KodaCare ao seu ambiente de trabalho.',
  'install.modal.dismiss': 'Talvez mais tarde',
  'features.eyebrow': 'TUDO O QUE PRECISA',
  'features.heading': 'Criado para a vida após o transplante',
  'features.subheading': 'KodaCare reúne todas as ferramentas de que precisa para gerir os cuidados diários do seu animal.',
  'features.vitals.title': 'Diário de Sinais Vitais',
  'features.vitals.desc':
    'Registe pressão arterial, peso, temperatura, ingestão de líquidos e débito urinário duas vezes por dia.',
  'features.meds.title': 'Centro de Medicamentos',
  'features.meds.desc': 'Acompanhe cada medicamento com formas visuais, dosagens e horários diários. Lembretes inteligentes garantem que nada seja esquecido — sem confusão, sem dose dupla.',
  'features.guide.title': 'Guia Digital',
  'features.guide.desc': 'Um manual pós-transplante abrangente — em 5 idiomas.',
  'features.notifications.title': 'Notificações Inteligentes',
  'features.notifications.desc': 'Lembretes pontuais para os seus registos de sinais vitais.',
  'features.trends.title': 'Tendências',
  'features.trends.desc': 'Gráficos bonitos transformam os seus dados em tendências significativas.',
  'features.rewards.title': 'Recompensas & Roomies',
  'features.rewards.desc': 'Mantenha-se motivado com recompensas de sequência.',
  'pricing.heading': 'Planos & Preços',
  'pricing.subheading': 'Comece gratuitamente durante 15 dias. Faça upgrade para manter os seus dados e aceder a registos ilimitados.',
  'pricing.monthly': 'MENSAL',
  'pricing.yearly': 'ANUAL',
  'pricing.mostPopular': 'MAIS POPULAR',
  'pricing.perMonth': '/mês',
  'pricing.choosePlan': 'Escolher plano',
  'pricing.startFree': 'Começar grátis',
  'pricing.currentPlan': 'O seu plano atual',
  'pricing.free.name': 'Inicial',
  'pricing.free.price': 'GRÁTIS',
  'pricing.free.subtitle': 'Teste de 15 dias — todas as funcionalidades incluídas. Após 15 dias, faça upgrade para manter os seus dados.',
  'pricing.vital.name': 'Companheiro Vital',
  'pricing.vital.subtitle': 'Registos ilimitados, armazenamento de documentos e acesso completo — tudo o que precisa a longo prazo.',
  'pricing.lifetime.name': 'Expert Companion',
  'pricing.lifetime.subtitle': 'Tudo do Vital, mais gráficos de tendências personalizáveis, um cofre de documentos maior e um digest mensal com as últimas novidades em nefrologia.',
  'pricing.feat.vitals': 'Diário de Sinais Vitais',
  'pricing.feat.meds': 'Centro de Medicamentos',
  'pricing.feat.notifications': 'Notificações',
  'pricing.feat.guide': 'Guia Digital',
  'pricing.feat.rewards': 'Recompensas & Roomies',
  'pricing.feat.endlessRecords': 'Registos ilimitados',
  'pricing.feat.vault50': 'Cofre de documentos (até 50 ficheiros)',
  'pricing.feat.vault150': 'Cofre de documentos (150 ficheiros)',
  'pricing.feat.vault5': 'Vet Records Vault (5 files)',
  'pricing.feat.customTrends': 'Customisable Extended Trends',
  'pricing.feat.trends': 'Trends & Charts',
  'pricing.feat.digest': 'Digest mensal de nefrologia',
  'pricing.feat.onePet': '1 Pet Profile',
  'pricing.feat.vitalPets': 'Up to 4 Pet Profiles',
  'pricing.feat.coCarers': '2 co-cuidadores por animal',
  'pricing.feat.fellowship': 'The Koda Fellowship',
  'pricing.feat.unlimitedPets': 'Até 30 perfis de animais',
  'pricing.feat.vetExport': 'Vet-Ready Data Exports',
  'blog.heading': 'Da equipa KodaCare',
  'blog.subheading': 'Perspectivas, atualizações e histórias da nossa comunidade.',
  'blog.readMore': 'Ler mais →',
  'blog.backToBlog': 'Voltar ao blog',
  'blog.minRead': 'min de leitura',
  'blog.moreFromTeam': 'Mais da equipa KodaCare',
  'blog.readAllArticles': 'Ler todos os artigos',
  'blog.tag.newNorms': 'Novos hábitos',
  'blog.tag.roomies': 'Roomies',
  'blog.tag.labs': 'Análises',
  'blog.tag.routines': 'Rotinas',
  'blog.authorTeam': 'A equipa KodaCare',
  'legal.eyebrow': 'Legal',
  'legal.lastUpdated': 'Última atualização',
  'legal.privacyTitle': 'Política de Privacidade',
  'legal.termsTitle': 'Termos de Serviço',
  'faq.heading': 'Perguntas frequentes',
  'faq.subheading': 'Tudo o que você precisa saber sobre a KodaCare. Se não encontrar a resposta aqui, fale com o nosso suporte.',
  'faq.stillHaveQuestions': 'Ainda tem dúvidas?',
  'faq.contactUs': 'Nossa equipa de suporte está aqui para ajudar.',
  'faq.emailSupport': 'Contactar o suporte',
  'faq.category.gettingStarted': 'Primeiros passos',
  'faq.category.vitals': 'Diário de sinais vitais',
  'faq.category.meds': 'Centro de medicamentos',
  'faq.category.guide': 'Guia digital',
  'faq.category.notifications': 'Notificações e recompensas',
  'faq.category.roomies': 'Roomies e comunidade',
  'faq.category.trends': 'Tendências e dados',
  'faq.category.security': 'Privacidade e segurança',
  'faq.category.pricing': 'Planos e recursos',
  'faq.category.technical': 'Suporte técnico',
  'faq.q.whatIsKodaCare': 'O que é a KodaCare?',
  'faq.a.whatIsKodaCare': 'A KodaCare é uma companheira digital de saúde animal, focada em rotina, medicação e acompanhamento diário.',
  'faq.q.whoCanUse': 'Quem pode usar a KodaCare?',
  'faq.a.whoCanUse': 'Principalmente pessoas transplantadas renais. Cuidadores também podem apoiar a rotina, conforme necessário.',
  'faq.q.howToSignUp': 'Como faço o cadastro?',
  'faq.a.howToSignUp': 'Aceda ao app ou website, toque em "Registar", confirme o e-mail e complete o perfil.',
  'faq.q.techSkills': 'Preciso ter conhecimento técnico?',
  'faq.a.techSkills': 'Não. O app foi desenhado para ser simples, claro e fácil de usar.',
  'faq.q.whatAreVitals': 'O que são sinais vitais e por que devo registá-los?',
  'faq.a.whatAreVitals': 'São medidas como pressão arterial, peso, temperatura, ingestão de líquidos e diurese. O registo consistente ajuda no acompanhamento clínico.',
  'faq.q.howOftenLog': 'Com que frequência devo registar?',
  'faq.a.howOftenLog': 'Normalmente duas vezes ao dia, conforme orientação da equipa de transplante.',
  'faq.q.normalRanges': 'Quais são os valores normais?',
  'faq.a.normalRanges': 'Os intervalos variam por pessoa. Siga sempre os parâmetros definidos pela sua equipa clínica.',
  'faq.q.fluidIntake': 'Por que acompanhar ingestão de líquidos?',
  'faq.a.fluidIntake': 'O controlo hídrico é essencial após o transplante e ajuda a interpretar melhor a evolução diária.',
  'faq.q.editDeleteLogs': 'Posso editar ou apagar registos?',
  'faq.a.editDeleteLogs': 'Sim. Pode editar ou remover entradas na secção de registos.',
  'faq.q.addMedication': 'Como adiciono medicamentos?',
  'faq.a.addMedication': 'Na secção Meds, toque em "Add Medication" e preencha nome, dose e horários.',
  'faq.q.immunosuppressants': 'Por que os imunossupressores são importantes?',
  'faq.a.immunosuppressants': 'Eles reduzem o risco de rejeição do enxerto. A adesão correta é fundamental no pós-transplante.',
  'faq.q.reminders': 'Como configuro lembretes?',
  'faq.a.reminders': 'Ao criar o medicamento, escolha os horários de notificação para cada dose.',
  'faq.q.missedDose': 'O que fazer se eu perder uma dose?',
  'faq.a.missedDose': 'Registe no app e contacte a equipa de transplante para orientação específica.',
  'faq.q.multipleUsers': 'Várias pessoas podem usar o mesmo dispositivo?',
  'faq.a.multipleUsers': 'Sim, mas cada pessoa deve ter a própria conta para manter os dados separados.',
  'faq.q.whatInGuide': 'O que há no guia digital?',
  'faq.a.whatInGuide': 'Conteúdos sobre alimentação, higiene, exercício, medicação e obstáculos da recuperação.',
  'faq.q.languages': 'Quais idiomas estão disponíveis?',
  'faq.a.languages': 'Inglês, francês, alemão, português e espanhol.',
  'faq.q.guideAccuracy': 'O guia é atualizado?',
  'faq.a.guideAccuracy': 'Sim. O conteúdo é revisado regularmente com apoio clínico.',
  'faq.q.updateGuide': 'Com que frequência o guia muda?',
  'faq.a.updateGuide': 'Atualizações são publicadas periodicamente com melhorias e novos conteúdos.',
  'faq.q.notificationTypes': 'Que notificações vou receber?',
  'faq.a.notificationTypes': 'Lembretes de medicação e de registo de sinais vitais.',
  'faq.q.customizeNotifications': 'Posso desativar notificações?',
  'faq.a.customizeNotifications': 'Sim. Você pode personalizar ou desativar nas definições.',
  'faq.q.twoNotifications': 'Por que estou a receber duas notificações ao mesmo tempo?',
  'faq.a.twoNotifications': 'É completamente normal na primeira configuração da KodaCare. O seu dispositivo envia dois pedidos de permissão separados — um do navegador e outro da própria aplicação. Se aceitou ambos, pode receber notificações duplicadas por um curto período. Elas vão sincronizar rapidamente. Se os duplicados persistirem, vá a Definições → Notificações, desative e volte a ativar.',
  'faq.q.whatIsKoda': 'O que é o Koda?',
  'faq.a.whatIsKoda': 'É o sistema de motivação da KodaCare, focado em consistência e responsabilidade.',
  'faq.q.earnXP': 'Como ganho XP?',
  'faq.a.earnXP': 'Registando sinais vitais, medicações, leitura de guias e manutenção de sequências.',
  'faq.q.streaks': 'O que são sequências?',
  'faq.a.streaks': 'Dias consecutivos de registo. Elas ajudam a consolidar hábitos saudáveis.',
  'faq.q.whatAreRoomies': 'O que são Roomies?',
  'faq.a.whatAreRoomies': 'São conexões com outros transplantados para apoio mútuo e motivação.',
  'faq.q.joinRoomies': 'Como entro num grupo Roomies?',
  'faq.a.joinRoomies': 'Em Definições → Roomies, crie ou introduza um código de grupo.',
  'faq.q.privacy': 'Meus dados ficam privados?',
  'faq.a.privacy': 'Sim. Você controla o que partilha e a partilha é sempre opcional.',
  'faq.q.leaveRoomies': 'Posso sair do Roomies?',
  'faq.a.leaveRoomies': 'Sim, pode desativar quando quiser.',
  'faq.q.whatAreTrends': 'O que são tendências?',
  'faq.a.whatAreTrends': 'Gráficos que mostram a evolução dos seus registos ao longo do tempo.',
  'faq.q.shareData': 'Posso partilhar dados com meu médico?',
  'faq.a.shareData': 'Sim. Pode exportar relatórios para revisão antes das consultas.',
  'faq.q.dataDownload': 'Posso descarregar todos os meus dados?',
  'faq.a.dataDownload': 'Sim, na área de dados nas definições.',
  'faq.q.dataEncrypted': 'Meus dados são protegidos?',
  'faq.a.dataEncrypted': 'Sim, com encriptação padrão de mercado em trânsito e em repouso.',
  'faq.q.whoAccess': 'Quem pode ver meus dados?',
  'faq.a.whoAccess': 'Somente você, salvo partilha explícita por sua escolha.',
  'faq.q.compliance': 'A KodaCare cumpre normas de privacidade?',
  'faq.a.compliance': 'Sim, alinhada a requisitos relevantes como GDPR e HIPAA.',
  'faq.q.deleteAccount': 'Posso apagar minha conta?',
  'faq.a.deleteAccount': 'Sim, nas definições da conta pode solicitar remoção permanente.',
  'faq.q.freePlan': 'Quanto tempo é gratuito?',
  'faq.a.freePlan': 'O plano Starter oferece 15 dias de acesso às funcionalidades principais.',
  'faq.q.planDifference': 'Qual a diferença entre os planos?',
  'faq.a.planDifference': 'Os planos variam em armazenamento e recursos de acompanhamento familiar.',
  'faq.q.upgrade': 'Como faço upgrade?',
  'faq.a.upgrade': 'Após o período inicial, pode escolher o plano nas definições de assinatura.',
  'faq.q.refund': 'Qual é a política de reembolso?',
  'faq.a.refund': 'Pedidos dentro de 30 dias são avaliados para reembolso total.',
  'faq.q.devices': 'Em quais dispositivos funciona?',
  'faq.a.devices': 'iPhone, Android, iPad e navegador web.',
  'faq.q.offline': 'Funciona offline?',
  'faq.a.offline': 'Sim, com sincronização automática quando houver internet.',
  'faq.q.syncData': 'Como funciona a sincronização?',
  'faq.a.syncData': 'Os dados sincronizam entre dispositivos em tempo real quando conectados.',
  'faq.q.dataRetention': 'Por quanto tempo os meus dados são guardados?',
  'faq.a.dataRetention': 'Os dados da conta ativa são guardados durante a vida da conta. Após eliminação, os seus dados pessoais são removidos em 30 dias. Registos agregados anonimizados podem ser mantidos até 6 meses por obrigações regulatórias de dispositivos médicos. Consulte a nossa Política de Privacidade para mais detalhes.',
  'faq.q.support': 'Como obter ajuda?',
  'faq.a.support': 'Use a secção de Ajuda nas Definições da aplicação. A nossa equipa responde normalmente em 24 horas.',
  'personas.eyebrow': 'DESIGNED FOR EVERY COMPANION',
  'personas.heading': 'Designed for Every Companion',
  'personas.subheading': 'KodaCare adapts to your life, whether you have one pup or a whole pasture.',
  'personas.modern.title': 'The Modern Pet Parent',
  'personas.modern.tag': '"Peace of Mind"',
  'personas.modern.desc': 'Gamified habits that turn daily care into a win. Perfect for tracking streaks and sharing milestones in the Fellowship.',
  'personas.elderly.title': 'The Elderly Dog Carer',
  'personas.elderly.tag': '"The Safety Net"',
  'personas.elderly.desc': 'Simple, bold visuals and multi-user alerts so the whole family can support the care of a beloved senior pet.',
  'personas.farm.title': 'The Modern Farm Keeper',
  'personas.farm.tag': '"The Herd Hub"',
  'personas.farm.desc': 'Professional-grade data management for multiple companions. Track herd health trends and export professional reports for your vet.',
  'footer.tagline': 'O hub digital para recuperação pós-transplante.',
  'footer.disclaimer': 'KodaCare é apenas uma ferramenta de monitorização de saúde animal.',
  'footer.product': 'Produto',
  'footer.account': 'Conta',
  'footer.legal': 'Legal',
  'footer.terms': 'Termos e Condições',
  'footer.privacy': 'Política de Privacidade',
  'footer.faq': 'FAQ',
  'footer.rightsReserved': 'Todos os direitos reservados.',
  'gdpr.title': 'Utilizamos cookies',
  'gdpr.body': 'Utilizamos cookies essenciais para o manter autenticado e lembrar as suas preferências. Cookies analíticos ajudam-nos a melhorar a KodaCare. Nenhum dado de saúde do seu animal é partilhado com anunciantes externos.',
  'gdpr.learnMore': 'Saiba mais na nossa Política de Privacidade',
  'gdpr.accept': 'Aceitar tudo',
  'gdpr.decline': 'Apenas essenciais',
};

const es: WebsiteTranslations = {
  'site.titleSuffix': 'Compañero de salud para trasplante renal',
  'site.description':
    'KodaCare ayuda a tutores de mascotas a registrar medicación, ingesta de agua y salud diaria, con guía práctica y paneles claros.',
  'nav.features': 'FUNCIONES',
  'nav.pricing': 'PRECIOS',
  'nav.stories': 'HISTORIAS',
  'nav.login': 'Iniciar sesión',
  'nav.signup': 'Registrarse',
  'nav.blog': 'Blog',
  'hero.eyebrow': 'HUB DE PACIENTE POST-TRASPLANTE',
  'hero.headline0': '',
  'hero.headline1': 'La recuperación es',
  'hero.headline2': 'un maratón.',
  'hero.headline3': 'No lo corras',
  'hero.headline4': 'sin KodaCare.',
  'hero.body':
    'El cuidado completo de tu mascota, simplificado. KodaCare te ayuda a estar al día con los medicamentos y las tendencias de salud de cada mascota en casa. Mantén el rumbo con facilidad y sube en los rangos de nuestra Fellowship de amantes de los animales.',
  'hero.cta': 'Probar gratis',
  'hero.cta.openApp': 'Abrir la app',
  'hero.cta.installOn': 'Instalar en',
  'hero.cta.installApp': 'Instalar la app',
  'hero.installTip.ios': "Toca Compartir en Safari y luego elige «Añadir a pantalla de inicio».",
  'hero.installTip.android': "Abre el menú del navegador y elige «Añadir a pantalla de inicio» o «Instalar aplicación».",
  'hero.installTip.default': "Busca el icono de instalación (⊕) en la barra de direcciones del navegador y haz clic para instalar.",
  'install.modal.title': 'Añadir KodaCare a tu dispositivo',
  'install.modal.subtitle': 'Disfruta la experiencia completa:',
  'install.modal.android.cta': 'Instalar',
  'install.modal.feature1': 'Acceso sin conexión',
  'install.modal.feature2': 'Notificaciones push',
  'install.modal.feature3': 'Apertura instantánea',
  'install.modal.android.hint': 'Pulsa el botón de arriba y confirma en el mensaje que aparezca.',
  'install.modal.android.step1': 'Pulsa el menú del navegador (⋮)',
  'install.modal.android.step2': "Pulsa 'Añadir a la pantalla de inicio'",
  'install.modal.ios.step1': 'Pulsa el botón Compartir en la parte inferior de la pantalla',
  'install.modal.ios.step2': "Desplázate hacia abajo y pulsa 'Añadir a la pantalla de inicio'",
  'install.modal.ios.step3': "Pulsa 'Añadir' en la esquina superior derecha para confirmar",
  'install.modal.ios.hint': 'Usa Safari para la mejor experiencia de instalación en iOS.',
  'install.modal.desktop.tip': 'Busca el icono de instalación en la barra de direcciones (parece una pantalla con flecha de descarga) y haz clic para añadir KodaCare a tu escritorio.',
  'install.modal.dismiss': 'Quizás más tarde',
  'features.eyebrow': 'TODO LO QUE NECESITAS',
  'features.heading': 'Diseñado para la vida tras el trasplante',
  'features.subheading': 'KodaCare reúne todas las herramientas que necesitas para gestionar el cuidado diario de tu mascota.',
  'features.vitals.title': 'Diario de Constantes',
  'features.vitals.desc':
    'Registra presión arterial, peso, temperatura, ingesta de líquidos y diuresis dos veces al día.',
  'features.meds.title': 'Centro de Medicación',
  'features.meds.desc': 'Registra cada medicamento con formas visuales, dosis y franjas horarias diarias. Los recordatorios inteligentes evitan confusiones y dobles dosis.',
  'features.guide.title': 'Guía Digital',
  'features.guide.desc': 'Un manual post-trasplante completo — en 5 idiomas.',
  'features.notifications.title': 'Notificaciones Inteligentes',
  'features.notifications.desc': 'Avisos oportunos para tus registros de constantes.',
  'features.trends.title': 'Tendencias',
  'features.trends.desc': 'Hermosos gráficos convierten tus datos en tendencias significativas.',
  'features.rewards.title': 'Recompensas & Roomies',
  'features.rewards.desc': 'Mantente motivado con recompensas de racha.',
  'pricing.heading': 'Planes & Precios',
  'pricing.subheading': 'Empieza gratis durante 15 días. Actualiza para conservar tus datos y acceder a registros ilimitados.',
  'pricing.monthly': 'MENSUAL',
  'pricing.yearly': 'ANUAL',
  'pricing.mostPopular': 'MÁS POPULAR',
  'pricing.perMonth': '/mes',
  'pricing.choosePlan': 'Elegir plan',
  'pricing.startFree': 'Empezar gratis',
  'pricing.currentPlan': 'Tu plan actual',
  'pricing.free.name': 'Inicio',
  'pricing.free.price': 'GRATIS',
  'pricing.free.subtitle': 'Prueba de 15 días — todas las funciones incluidas. Tras 15 días, actualiza para conservar tus datos.',
  'pricing.vital.name': 'Compañero Vital',
  'pricing.vital.subtitle': 'Registros ilimitados, almacenamiento de documentos y acceso completo — todo lo que necesitas a largo plazo.',
  'pricing.lifetime.name': 'Expert Companion',
  'pricing.lifetime.subtitle': 'Todo lo de Vital, más gráficos de tendencias personalizables, una bóveda de documentos más grande y un digest mensual con las últimas novedades en nefrología.',
  'pricing.feat.vitals': 'Diario de Constantes',
  'pricing.feat.meds': 'Centro de Medicación',
  'pricing.feat.notifications': 'Notificaciones',
  'pricing.feat.guide': 'Guía Digital',
  'pricing.feat.rewards': 'Recompensas & Roomies',
  'pricing.feat.endlessRecords': 'Registros ilimitados',
  'pricing.feat.vault50': 'Bóveda de documentos (hasta 50 archivos)',
  'pricing.feat.vault150': 'Bóveda de documentos (150 archivos)',
  'pricing.feat.vault5': 'Vet Records Vault (5 files)',
  'pricing.feat.customTrends': 'Customisable Extended Trends',
  'pricing.feat.trends': 'Trends & Charts',
  'pricing.feat.digest': 'Digest mensual de nefrología',
  'pricing.feat.onePet': '1 Pet Profile',
  'pricing.feat.vitalPets': 'Up to 4 Pet Profiles',
  'pricing.feat.coCarers': '2 co-cuidadores por mascota',
  'pricing.feat.fellowship': 'The Koda Fellowship',
  'pricing.feat.unlimitedPets': 'Hasta 30 perfiles de mascotas',
  'pricing.feat.vetExport': 'Vet-Ready Data Exports',
  'blog.heading': 'Del equipo KodaCare',
  'blog.subheading': 'Perspectivas, actualizaciones e historias de nuestra comunidad.',
  'blog.readMore': 'Leer más →',
  'blog.backToBlog': 'Volver al blog',
  'blog.minRead': 'min de lectura',
  'blog.moreFromTeam': 'Más del equipo KodaCare',
  'blog.readAllArticles': 'Leer todos los artículos',
  'blog.tag.newNorms': 'Nuevas normas',
  'blog.tag.roomies': 'Roomies',
  'blog.tag.labs': 'Análisis',
  'blog.tag.routines': 'Rutinas',
  'blog.authorTeam': 'El equipo de KodaCare',
  'legal.eyebrow': 'Legal',
  'legal.lastUpdated': 'Última actualización',
  'legal.privacyTitle': 'Política de Privacidad',
  'legal.termsTitle': 'Términos del Servicio',
  'faq.heading': 'Preguntas frecuentes',
  'faq.subheading': 'Todo lo que necesitas saber sobre KodaCare. Si no encuentras tu respuesta aquí, nuestro equipo de soporte puede ayudarte.',
  'faq.stillHaveQuestions': '¿Aún tienes preguntas?',
  'faq.contactUs': 'Nuestro equipo de soporte está listo para ayudarte.',
  'faq.emailSupport': 'Contactar soporte',
  'faq.category.gettingStarted': 'Primeros pasos',
  'faq.category.vitals': 'Registro de constantes',
  'faq.category.meds': 'Centro de medicación',
  'faq.category.guide': 'Guía digital',
  'faq.category.notifications': 'Notificaciones y recompensas',
  'faq.category.roomies': 'Roomies y comunidad',
  'faq.category.trends': 'Tendencias y datos',
  'faq.category.security': 'Privacidad y seguridad',
  'faq.category.pricing': 'Planes y funciones',
  'faq.category.technical': 'Soporte técnico',
  'faq.q.whatIsKodaCare': '¿Qué es KodaCare?',
  'faq.a.whatIsKodaCare': 'KodaCare es un acompañante digital de salud para mascotas, pensado para organizar hábitos, medicación y seguimiento diario.',
  'faq.q.whoCanUse': '¿Quién puede usar KodaCare?',
  'faq.a.whoCanUse': 'Principalmente receptores de trasplante renal. También puede ser útil para cuidadores en tareas de apoyo.',
  'faq.q.howToSignUp': '¿Cómo me registro?',
  'faq.a.howToSignUp': 'Entra en la app o en la web, pulsa "Registrarse", verifica tu correo y completa tu perfil.',
  'faq.q.techSkills': '¿Necesito conocimientos técnicos?',
  'faq.a.techSkills': 'No. La app está diseñada para ser intuitiva y fácil de usar desde el primer día.',
  'faq.q.whatAreVitals': '¿Qué son las constantes y por qué debo registrarlas?',
  'faq.a.whatAreVitals': 'Son medidas como presión arterial, peso, temperatura, ingesta de líquidos y diuresis. Ayudan a detectar cambios con más contexto.',
  'faq.q.howOftenLog': '¿Con qué frecuencia debo registrar?',
  'faq.a.howOftenLog': 'Por lo general, dos veces al día, siguiendo la indicación de tu equipo de trasplante.',
  'faq.q.normalRanges': '¿Cuáles son los valores normales?',
  'faq.a.normalRanges': 'Dependen de cada persona. Toma como referencia los rangos definidos por tu equipo clínico.',
  'faq.q.fluidIntake': '¿Por qué registrar líquidos?',
  'faq.a.fluidIntake': 'El balance hídrico es clave tras el trasplante y aporta contexto útil para interpretar tu evolución.',
  'faq.q.editDeleteLogs': '¿Puedo editar o borrar registros?',
  'faq.a.editDeleteLogs': 'Sí. Puedes corregir o eliminar entradas desde la sección de registros.',
  'faq.q.addMedication': '¿Cómo agrego medicamentos?',
  'faq.a.addMedication': 'En Meds, pulsa "Add Medication" y añade nombre, dosis y horarios.',
  'faq.q.immunosuppressants': '¿Por qué son importantes los inmunosupresores?',
  'faq.a.immunosuppressants': 'Disminuyen el riesgo de rechazo del injerto. Tomarlos correctamente es esencial.',
  'faq.q.reminders': '¿Cómo configuro recordatorios?',
  'faq.a.reminders': 'Al crear cada medicamento, elige la hora de cada aviso.',
  'faq.q.missedDose': '¿Qué hago si olvido una dosis?',
  'faq.a.missedDose': 'Regístralo en la app y contacta a tu equipo de trasplante para la pauta adecuada.',
  'faq.q.multipleUsers': '¿Varias personas pueden usar el mismo dispositivo?',
  'faq.a.multipleUsers': 'Sí, pero cada persona debe tener su propia cuenta.',
  'faq.q.whatInGuide': '¿Qué incluye la guía digital?',
  'faq.a.whatInGuide': 'Información sobre alimentación, higiene, ejercicio, medicación y obstáculos frecuentes.',
  'faq.q.languages': '¿Qué idiomas están disponibles?',
  'faq.a.languages': 'Inglés, francés, alemán, portugués y español.',
  'faq.q.guideAccuracy': '¿La guía está actualizada?',
  'faq.a.guideAccuracy': 'Sí. El contenido se revisa periódicamente con criterio clínico.',
  'faq.q.updateGuide': '¿Se actualiza la guía?',
  'faq.a.updateGuide': 'Sí, de forma regular con mejoras y nuevos contenidos.',
  'faq.q.notificationTypes': '¿Qué notificaciones recibiré?',
  'faq.a.notificationTypes': 'Recordatorios de medicación y avisos para registrar constantes.',
  'faq.q.customizeNotifications': '¿Puedo desactivar notificaciones?',
  'faq.a.customizeNotifications': 'Sí, puedes personalizarlas o apagarlas desde ajustes.',
  'faq.q.twoNotifications': '¿Por qué recibo dos notificaciones a la vez?',
  'faq.a.twoNotifications': 'Es completamente normal al configurar KodaCare por primera vez. Tu dispositivo envía dos solicitudes de permiso por separado — una del navegador y otra de la propia aplicación. Si aceptaste ambas, puede que recibas notificaciones duplicadas brevemente. Se sincronizarán pronto. Si los duplicados persisten, ve a Ajustes → Notificaciones, desactívalas y vuelve a activarlas.',
  'faq.q.whatIsKoda': '¿Qué es Koda?',
  'faq.a.whatIsKoda': 'Es el sistema de motivación de KodaCare para reforzar constancia y adherencia.',
  'faq.q.earnXP': '¿Cómo gano XP?',
  'faq.a.earnXP': 'Registrando constantes, medicación, leyendo guías y manteniendo rachas.',
  'faq.q.streaks': '¿Qué son las rachas?',
  'faq.a.streaks': 'Días consecutivos con actividad registrada. Ayudan a consolidar hábitos.',
  'faq.q.whatAreRoomies': '¿Qué son los Roomies?',
  'faq.a.whatAreRoomies': 'Conexiones con otros trasplantados para apoyo mutuo y seguimiento compartido.',
  'faq.q.joinRoomies': '¿Cómo entro a un grupo Roomies?',
  'faq.a.joinRoomies': 'En Ajustes → Roomies puedes crear o unirte con un código de grupo.',
  'faq.q.privacy': '¿Mis datos siguen siendo privados?',
  'faq.a.privacy': 'Sí. Tú decides qué compartir y la participación siempre es voluntaria.',
  'faq.q.leaveRoomies': '¿Puedo salir de Roomies?',
  'faq.a.leaveRoomies': 'Sí, puedes desactivarlo en cualquier momento.',
  'faq.q.whatAreTrends': '¿Qué son las tendencias?',
  'faq.a.whatAreTrends': 'Gráficos que muestran la evolución de tus datos a lo largo del tiempo.',
  'faq.q.shareData': '¿Puedo compartir datos con mi equipo médico?',
  'faq.a.shareData': 'Sí, puedes exportar informes y enviarlos antes de las consultas.',
  'faq.q.dataDownload': '¿Puedo descargar todos mis datos?',
  'faq.a.dataDownload': 'Sí, desde la sección de datos en ajustes.',
  'faq.q.dataEncrypted': '¿Los datos están cifrados?',
  'faq.a.dataEncrypted': 'Sí, con estándares de seguridad para transmisión y almacenamiento.',
  'faq.q.whoAccess': '¿Quién puede ver mi información?',
  'faq.a.whoAccess': 'Solo tú, salvo que compartas de forma explícita.',
  'faq.q.compliance': '¿Cumple normas de privacidad?',
  'faq.a.compliance': 'Sí, está alineado con marcos como HIPAA y GDPR.',
  'faq.q.deleteAccount': '¿Puedo eliminar mi cuenta?',
  'faq.a.deleteAccount': 'Sí, puedes solicitar eliminación permanente desde ajustes de cuenta.',
  'faq.q.freePlan': '¿Cuánto dura el plan gratuito?',
  'faq.a.freePlan': 'El plan Starter incluye 15 días de acceso a funciones principales.',
  'faq.q.planDifference': '¿Qué diferencia hay entre planes?',
  'faq.a.planDifference': 'Cambian principalmente el almacenamiento y funciones para acompañamiento familiar.',
  'faq.q.upgrade': '¿Cómo actualizo mi plan?',
  'faq.a.upgrade': 'Después del periodo inicial, puedes elegir plan desde la sección de suscripción.',
  'faq.q.refund': '¿Cuál es la política de reembolso?',
  'faq.a.refund': 'Las solicitudes dentro de 30 días se evalúan para reembolso completo.',
  'faq.q.devices': '¿En qué dispositivos funciona?',
  'faq.a.devices': 'iPhone, Android, iPad y navegador web.',
  'faq.q.offline': '¿Funciona sin internet?',
  'faq.a.offline': 'Sí, y sincroniza automáticamente cuando vuelves a tener conexión.',
  'faq.q.syncData': '¿Cómo funciona la sincronización?',
  'faq.a.syncData': 'Los datos se sincronizan entre dispositivos en tiempo real cuando hay conexión.',
  'faq.q.dataRetention': '¿Cuánto tiempo se guardan mis datos?',
  'faq.a.dataRetention': 'Los datos de la cuenta activa se conservan durante la vida de la cuenta. Tras la eliminación, tus datos personales se borran en 30 días. Los registros agregados anonimizados pueden conservarse hasta 6 meses por obligaciones regulatorias de dispositivos médicos. Consulta nuestra Política de Privacidad para más detalles.',
  'faq.q.support': '¿Cómo obtengo ayuda?',
  'faq.a.support': 'Usa la sección de Ayuda en los Ajustes de la aplicación. Nuestro equipo suele responder en 24 horas.',
  'personas.eyebrow': 'DESIGNED FOR EVERY COMPANION',
  'personas.heading': 'Designed for Every Companion',
  'personas.subheading': 'KodaCare adapts to your life, whether you have one pup or a whole pasture.',
  'personas.modern.title': 'The Modern Pet Parent',
  'personas.modern.tag': '"Peace of Mind"',
  'personas.modern.desc': 'Gamified habits that turn daily care into a win. Perfect for tracking streaks and sharing milestones in the Fellowship.',
  'personas.elderly.title': 'The Elderly Dog Carer',
  'personas.elderly.tag': '"The Safety Net"',
  'personas.elderly.desc': 'Simple, bold visuals and multi-user alerts so the whole family can support the care of a beloved senior pet.',
  'personas.farm.title': 'The Modern Farm Keeper',
  'personas.farm.tag': '"The Herd Hub"',
  'personas.farm.desc': 'Professional-grade data management for multiple companions. Track herd health trends and export professional reports for your vet.',
  'footer.tagline': 'El hub digital para recuperación post-trasplante.',
  'footer.disclaimer': 'KodaCare es una herramienta de seguimiento de salud para mascotas.',
  'footer.product': 'Producto',
  'footer.account': 'Cuenta',
  'footer.legal': 'Legal',
  'footer.terms': 'Términos y Condiciones',
  'footer.privacy': 'Política de Privacidad',
  'footer.faq': 'Preguntas frecuentes',
  'footer.rightsReserved': 'Todos los derechos reservados.',
  'gdpr.title': 'Usamos cookies',
  'gdpr.body': 'Usamos cookies esenciales para mantener tu sesión iniciada y recordar tus preferencias. Las cookies analíticas nos ayudan a mejorar KodaCare. Ningún dato de salud de tu mascota se comparte con anunciantes externos.',
  'gdpr.learnMore': 'Más información en nuestra Política de Privacidad',
  'gdpr.accept': 'Aceptar todo',
  'gdpr.decline': 'Solo esenciales',
};

const ALL: Record<SupportedLanguage, WebsiteTranslations> = {
  English: en,
  French: fr,
  German: de,
  Portuguese: pt,
  Spanish: es,
};

export const WEBSITE_TRANSLATIONS_BY_LANGUAGE = ALL;

export function wt(language: SupportedLanguage, key: keyof WebsiteTranslations): string {
  return ALL[language]?.[key] ?? ALL.English[key] ?? key;
}
